import { useState, useMemo, useEffect, useCallback, useRef, Fragment } from "react";
import { useNavigate } from 'react-router-dom';
import { useModel } from '../ModelProvider';
import { moduleLineLabel } from '../utils/leafLabels';
import OnboardingIncomplete from '../components/OnboardingIncomplete';
import ModelLoadError from '../components/ModelLoadError';
import ViewOnly, { ViewOnlyNote } from '../components/ViewOnly';
import { getEmployeesWithActuals, patchEmployee, getCompanySettings, updateCompanySettings, updateCompanyQuotaSettings, getAEQuotaSettings, updateAEQuotaSettings, createMiscRevenueLine, updateMiscRevenueLine, deleteMiscRevenueLine, reorderMiscRevenueLines, createImplementationLine, updateImplementationLine, deleteImplementationLine } from '../api';
import { filterActuals, filterForecast, getYear, formatMonthLabel } from '../engines/dateUtils';
import { RenewalModeSelect, ChurnRateInput, ManualRenewalsGrid } from '../components/RenewalControls';
import { implSyntheticKey, isRoutedElsewhere } from '../utils/implKeys';
import { miscSyntheticKey } from '../utils/miscKeys';
import { normalizeQuotaPattern, monthlyQuotaAmount, validateQuotaPattern, quotaGridYears, pruneToGrid, seedEvenYear, factorsByYear } from '../engines/quotaDistribution';
import { miscTargetOptions, miscTargetLabel, claimedRevenueTargets } from '../utils/miscTargets';
import { parseTakeRateText, takeRateText } from '../utils/implTakeRate';
import { IMPL_BASIS_PER_CLIENT, IMPL_BASIS_PCT_NEW_ACV, IMPL_BASIS_LABELS, IMPL_BASIS_BLANK_LABEL, IMPL_BASIS_REQUIRED_MESSAGE, PCT_NEW_ACV_LABEL, PCT_NEW_ACV_HELP, PCT_NEW_ACV_REQUIRED_MESSAGE, isPctNewAcvBasis, parsePctNewAcvText, pctNewAcvText, implBasisErrorMessage } from '../utils/implBasis';
import { growthTypeSwitch } from '../utils/growthTypeSwitch';
import { reseedDrafts, sameValue } from '../utils/keepDirtyDraft';
import { useSettingsStamp, stampConflictMessage } from '../utils/settingsStamp';
import { handleRowPaste, PASTE_ERROR_STYLE } from '../utils/pasteRow';   // Batch 1b (L3593): the misc month boxes take a pasted Excel row
import PasteHint from '../utils/PasteHint';

/* ═══════════════════════════════════════════════════════════════════════════════
   REVENUE BUILD MODULE — SaaS Subscription ARR/MRR Forecast
   ═══════════════════════════════════════════════════════════════════════════════ */

const r2 = n => Math.round(n * 100) / 100;
const fmt = n => {if(!n||n===0) return "—";const v=r2(n);const s=new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",minimumFractionDigits:2,maximumFractionDigits:2}).format(Math.abs(v));return v<0?`(${s})`:s;};
const fmtK = n => {if(!n||n===0) return "—";const v=r2(n);const a=Math.abs(v);let s;if(a>=1e6) s=`$${(a/1e6).toFixed(2)}M`;else if(a>=1e4) s=`$${(a/1e3).toFixed(1)}K`;else s=new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",minimumFractionDigits:2,maximumFractionDigits:2}).format(a);return v<0?`(${s})`:s;};
const fmtPct = n => !n&&n!==0?"—":(n*100).toFixed(1)+"%";
// an implementation line's STORED percentage of new ACV as a number (the GET returns numeric(6,4) as text), or null when unanswered
const storedPctOf = l => (l && l.pct_of_new_acv !== null && l.pct_of_new_acv !== undefined && l.pct_of_new_acv !== '' && Number.isFinite(Number(l.pct_of_new_acv))) ? Number(l.pct_of_new_acv) : null;

/* Group 3 F8.6 (2026-10-02): the legacy ARR / MRR model is DELETED here -- ACTUALS_DATA, EMPTY_FORECAST (the
 * Semitek defaults 0.134 / 0.55 / 0.62 / 50,791.20 / 276,917.45), DB_TO_ACTUALS and compute(). They fed only the
 * four legacy views (Dashboard / ARR Waterfall / Forecast Inputs / GAAP Revenue), which no tab reached since P4-a. */

/* ═══════════════════════════════════════════════════════════════════════════════
   COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════════ */
/* W-047.4: the private Spark copy is DELETED. Group 3 F8.6: its only use here (the legacy Dashboard view) is gone,
   so this page no longer imports components/Spark.jsx. DollarInput / PctInput went with the legacy views too. */

function SeasonalPctInput({value, onChange, style: st}) {
  const [ed, setEd] = useState(false);
  const [raw, setRaw] = useState("");
  const display = ((parseFloat(value)||0)*100).toFixed(2);
  return <input type="text" inputMode="decimal" style={st}
    value={ed ? raw : display}
    onFocus={e=>{setEd(true); setRaw(display); setTimeout(()=>e.target.select(),0);}}
    onChange={e=>{const v=e.target.value.replace(/[^0-9.]/g,""); setRaw(v);}}
    onBlur={()=>{setEd(false); const v=parseFloat(raw); onChange(isNaN(v)?0:v/100);}}/>;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function RevenueBuildPage({ companyId }) {
  const { markStale, ensureFresh, companyQuotaDR, aeQuotaDR, cutoff, rawData, miscRevenueLines: ctxMiscLines, implementationLines: ctxImplLines, implementationRevenue: ctxImplRevenue, onboardingGap, modelState, modelError, canEditModel } = useModel();
  const allMonths = useMemo(() => rawData?.allMonths || [], [rawData]);
  const actualsMonths = useMemo(() => filterActuals(allMonths, cutoff || ""), [allMonths, cutoff]);
  const forecastMonths = useMemo(() => filterForecast(allMonths, cutoff || ""), [allMonths, cutoff]);
  // Forecast-year-range label: single year (e.g. "2026") or range (e.g. "2025–2026").
  const forecastYearRangeLabel = useMemo(() => {
    if (!forecastMonths.length) return "";
    const firstY = getYear(forecastMonths[0]);
    const lastY = getYear(forecastMonths[forecastMonths.length - 1]);
    return firstY === lastY ? String(firstY) : `${firstY}–${lastY}`;
  }, [forecastMonths]);

  const navigate = useNavigate();
  /* ★ G6c — a deep link from Step 9 opens the tab it sent you for. Read ONCE, at mount,
   * from the query string: this is an entry-point concern, not reactive state, and reading
   * window.location.search in a lazy initialiser avoids adding a router hook to a page that
   * does not otherwise need one. An unknown or absent ?tab= falls back to the default, so a
   * stale or hand-edited URL can never land on a tab that does not exist. */
  const [view, setView] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["ae_quota", "company_quota", "misc_rev", "impl"].includes(t) ? t : "ae_quota";
  });
  /* ★ P4-a — the `legacy` derive, the four legacy tabs (Dashboard / ARR Waterfall /
   * Forecast Inputs / GAAP Revenue) and the default-tab flip effect are DELETED. The flag
   * they were gated on is false on all four live tenants and has no writer, so those tabs
   * were never offered and the effect always fired. The default is now "ae_quota" directly.
   * ⓘ `effectiveView` (below) existed only to map a legacy view -> ae_quota at RENDER time,
   *   because the effect resolved after the first paint and left a blank frame. With no
   *   legacy view reachable it is now always `view`. */
  // Option C — effective view for RENDER. The flip effect above corrects the underlying
  // `view` STATE, but effects resolve AFTER render: on a modern tenant the first painted
  // frame still has view="dashboard" (a now-hidden legacy tab) → blank. effectiveView maps
  // any legacy view → ae_quota at render time, independent of effect timing, so the page
  const effectiveView = view;
  /* Group 3 F8.6: the legacy revenue-model loader (fc / loading / modelId / loadData) is DELETED. It called
     getRevenueModels on every mount (always [] -- no tenant had a revenue model) and fed only the legacy views. */

  // ═══ AE QUOTA STATE ═══
  const [salesReps, setSalesReps] = useState([]);
  const [aeLoading, setAeLoading] = useState(false);
  /* The AE tab loads the sales reps ONCE PER COMPANY (2026-10-01). repsLoadedFor records the company the reps
   * were last loaded for, set when a load COMPLETES -- with reps, with none, or failed -- so the auto-load below
   * cannot re-fire for a roster that is simply empty. Before this, 0 reps kept `salesReps.length === 0` true after
   * every load and the effect reloaded forever (and a failed load the same). A company switch remounts the page
   * (App keys the provider on the company), so it loads once more; the Refresh / Retry buttons reload on demand. */
  const repsLoadedFor = useRef(null);
  const [repsError, setRepsError] = useState(null);
  const [aeSaving, setAeSaving] = useState(false);
  const [aeErrors, setAeErrors] = useState({}); // year → message, for the company pattern
  /* ★★ THE COMPANY-WIDE QUOTA PATTERN (Brian, 2026-09-15, shape A).
   * ONE dropdown for the whole company — ⛔ there is no per-rep distribution any more.
   * Seeded from the stored `ae_quota_settings.quota_distribution` through the SAME
   * normaliser the engine uses, so the screen and the model cannot disagree about what
   * "seasonal" means. */
  const [quotaPattern, setQuotaPattern] = useState(() => normalizeQuotaPattern(rawData?.aeqSettings?.quota_distribution));
  const [expandedRamps, setExpandedRamps] = useState({}); // repId → true for expanded ramp tooltip
  const [commissionDelay, setCommissionDelay] = useState(0); // 0, 1, or 2 months
  const [aeDirty, setAeDirty] = useState(false);

  // ═══ AE QUOTA ENABLE STATE ═══
  const [aeQuotaEnabled, setAeQuotaEnabled] = useState(true);
  const [aeQuotaEnableSaving, setAeQuotaEnableSaving] = useState(false);
  // 2026-10-01: the held stamp of each quota row (utils/settingsStamp.js) -- a save is refused if someone else saved the row
  // since this screen loaded -- and that refusal, shown beside the control that was used ({ at: 'toggle' | 'save', msg }).
  const aeStamp = useSettingsStamp(rawData?.aeqSettings);
  const cqStamp = useSettingsStamp(rawData?.cqSettings);
  const [aeConflict, setAeConflict] = useState(null);
  const [cqConflict, setCqConflict] = useState(null);
  const [miscPasteErr, setMiscPasteErr] = useState({});   // Batch 1b (L3593): lineId -> the paste refusal shown under that line's month boxes
  const [showAeDisableWarning, setShowAeDisableWarning] = useState(false);

  useEffect(() => {
    const s = rawData?.aeqSettings;
    if (s) setAeQuotaEnabled(s.enabled !== false);
  }, [rawData?.aeqSettings]);

  const toggleAeQuotaEnabled = useCallback(async (newVal) => {
    if (!newVal && rawData?.aeqSettings) {
      const s = rawData.aeqSettings;
      const hasData = (parseFloat(s.opening_bs_balance) || 0) > 0 || Object.values(s.existing_total_acv || {}).some(v => (parseFloat(v) || 0) > 0);
      if (hasData) { setShowAeDisableWarning(true); return; }
    }
    setAeQuotaEnableSaving(true);
    try {
      // Allowlist payload: send ONLY the field this toggle owns. Spreading
      // ...rawData.aeqSettings would send DefRev-owned engine-consumed fields
      // (existing_total_acv/existing_monthly_rev/existing_arr/opening_bs_balance)
      // as PRESENT values, bypassing the server's undefined→NULL COALESCE guards —
      // a stale rawData could then clobber a concurrent DefRev edit. Server COALESCE
      // preserves every omitted column, so { enabled } cannot null anything.
      aeStamp.adopt(await updateAEQuotaSettings(companyId, aeStamp.withStamp({ enabled: newVal })));
      setAeConflict(null);
      setAeQuotaEnabled(newVal);
      markStale();
      await ensureFresh();
    } catch (err) {
      console.error('Failed to toggle AE Quota:', err);
      const msg = stampConflictMessage(err); if (msg) setAeConflict({ at: 'toggle', msg });
    }
    finally { setAeQuotaEnableSaving(false); }
  }, [companyId, rawData?.aeqSettings, markStale, ensureFresh, aeStamp]);

  const confirmAeDisable = useCallback(async () => {
    setShowAeDisableWarning(false);
    setAeQuotaEnableSaving(true);
    try {
      // Allowlist payload — see toggleAeQuotaEnabled. Send only { enabled };
      // server COALESCE preserves every DefRev-owned column on omit.
      aeStamp.adopt(await updateAEQuotaSettings(companyId, aeStamp.withStamp({ enabled: false })));
      setAeConflict(null);
      setAeQuotaEnabled(false);
      markStale();
      await ensureFresh();
    } catch (err) {
      console.error('Failed to disable AE Quota:', err);
      const msg = stampConflictMessage(err); if (msg) setAeConflict({ at: 'toggle', msg });
    }
    finally { setAeQuotaEnableSaving(false); }
  }, [companyId, rawData?.aeqSettings, markStale, ensureFresh, aeStamp]);

  // ═══ COMPANY QUOTA STATE ═══
  const [cqEnabled, setCqEnabled] = useState(false);
  const [cqStartMode, setCqStartMode] = useState('zero');
  const [cqOpeningArr, setCqOpeningArr] = useState(0);
  const [cqGrowthType, setCqGrowthType] = useState('flat');
  const [cqGrowthMode, setCqGrowthMode] = useState('compounding');
  const [cqGrowthInputs, setCqGrowthInputs] = useState({});
  const [cqGrowthPeriod, setCqGrowthPeriod] = useState('individual');
  /* C1 -- cqAnnualGrowth IS RETIRED. It was a SECOND source of truth for the growth rate,
     alongside cqGrowthInputs, and its useState(0) initial value was never seeded on any
     live tenant (the load inference that would have seeded it required exactly 12 values
     and every tenant has 9). Selecting "Full Year" and pressing Save without typing
     therefore wrote 0 to EVERY forecast month, wiping the stored rate with no warning.
     cqGrowthInputs is now the ONE map the panel reads, writes and saves (Principle 0);
     "Full Year" FILLS it on selection rather than shadowing it. */
  const [cqRenewalMode, setCqRenewalMode] = useState('auto');
  const [cqChurnRate, setCqChurnRate] = useState(0);
  const [cqManualRenewals, setCqManualRenewals] = useState({});
  const [cqOpeningBal, setCqOpeningBal] = useState(0);
  const [cqExistingACV, setCqExistingACV] = useState({});
  const [cqExistingRev, setCqExistingRev] = useState({});
  const [cqSaving, setCqSaving] = useState(false);
  const [showCqDisableWarning, setShowCqDisableWarning] = useState(false);

  // Sync CQ local state from rawData.cqSettings (refreshed after ensureFresh)
  /* W-031 (C): a refetch re-seeds a field only when its draft is CLEAN (utils/keepDirtyDraft). A save on another tab of
     this page refetches the model; it used to overwrite every draft here, wiping an unsaved Company Quota edit.
     cqPrevRef holds the previous stored snapshot; cqForceRef holds the fields this tab's own save just sent. */
  const cqPrevRef = useRef(null);
  const cqForceRef = useRef(null);
  useEffect(() => {
    if (!rawData?.cqSettings) return;
    const s = rawData.cqSettings;
    const next = {
      enabled: !!s.enabled,
      start_mode: s.start_mode || 'zero',
      opening_arr: parseFloat(s.opening_arr) || 0,
      growth_type: s.growth_type || 'flat',
      growth_mode: s.growth_mode || 'compounding',
      growth_inputs: s.growth_inputs || {},
      renewal_mode: s.renewal_mode || 'auto',
      churn_rate: parseFloat(s.churn_rate) || 0,
      manual_renewals: s.manual_renewals || {},
      opening_bs_balance: parseFloat(s.opening_bs_balance) || 0,
      existing_total_acv: s.existing_total_acv || {},
      existing_monthly_rev: s.existing_monthly_rev || {},
    /* THE GROWTH PERIOD IS NOW READ, NOT INFERRED.

       WHAT WAS HERE, and it was mine, and it was wrong:
           const allSame = vals.length > 0 && vals.every(v => v === vals[0]);
           setCqGrowthPeriod(allSame ? 'annual' : 'individual');
       TRUE as a description of the data, WRONG as a decision. Brian's workflow is "choose
       Individual and type in the same month, then change certain months to play around
       with it" -- so an operator who deliberately types one value in every month was
       OVERRULED on reload: the select flipped to "Full Year" and the grid collapsed to a
       single box. The app has no business deducing intent from the shape of the data (E1).

       IT IS REMOVED, NOT NARROWED. Any rule that reads values to guess a mode gets it
       wrong the moment someone types uniform numbers on purpose; a tighter rule would
       only move the point at which it is wrong.

       NULL -> 'individual', DELIBERATELY. NULL means the operator has never answered (the
       column is defaultless precisely so that state is expressible). 'individual' is the
       safe render: it shows the grid, so nothing is hidden, and every stored month is
       visible and editable. 'annual' is the mode that OVERWRITES months when selected, so
       defaulting to it would apply an operator action nobody took. */
      growth_period: s.growth_period === 'annual' || s.growth_period === 'individual'
        ? s.growth_period : 'individual',
    };
    reseedDrafts({
      enabled: setCqEnabled, start_mode: setCqStartMode, opening_arr: setCqOpeningArr, growth_type: setCqGrowthType,
      growth_mode: setCqGrowthMode, growth_inputs: setCqGrowthInputs, renewal_mode: setCqRenewalMode,
      churn_rate: setCqChurnRate, manual_renewals: setCqManualRenewals, opening_bs_balance: setCqOpeningBal,
      existing_total_acv: setCqExistingACV, existing_monthly_rev: setCqExistingRev, growth_period: setCqGrowthPeriod,
    }, cqPrevRef.current, next, cqForceRef.current);
    cqPrevRef.current = next;
    cqForceRef.current = null;
  }, [rawData?.cqSettings]);

  /* C1 -- "Full Year (same value)" FILLS THE MAP THE MOMENT IT IS SELECTED.
     Brian's intent for the control was "to not have to type the same number in for all the
     forecast periods". It previously changed only which INPUT was rendered; the map it
     claimed to describe was untouched until Save, and Save read a different variable.
     THE FILL VALUE IS THE FIRST FORECAST MONTH'S CURRENT VALUE -- never 0, and never a
     separate state's initial value, because either of those IS the wipe by another route.
     An empty or blank first month yields 0, which is honest: there was nothing to preserve.
     Read functionally from `prev` so it can never act on a stale closure.
     SWITCHING BACK TO 'individual' CLEARS NOTHING -- it returns early, so the filled values
     simply become individually editable again. */
  const applyGrowthPeriod = useCallback(period => {
    setCqGrowthPeriod(period);
    if (period !== 'annual' || !forecastMonths.length) return;
    setCqGrowthInputs(prev => {
      const first = parseFloat(prev[forecastMonths[0]]) || 0;
      const next = {};
      forecastMonths.forEach(m => { next[m] = first; });
      return next;
    });
  }, [forecastMonths]);

  const saveCqSettings = useCallback(async () => {
    setCqSaving(true);
    try {
      /* C1 -- ONE SOURCE OF TRUTH FOR WHAT GETS SAVED: cqGrowthInputs.
         This used to branch on cqGrowthPeriod and, in the 'annual' arm, REBUILD the map
         from cqAnnualGrowth -- the retired second state. What the operator SAW and what
         the payload CARRIED were therefore different objects, which is how a blank box
         saved zeros over a stored rate. applyGrowthPeriod fills this same map on
         selection, so the grid always shows exactly what will be written. */
      const gi = cqGrowthInputs;
      // ALLOWLIST payload (KPI 6a Cp2 ownership split, hardened to match AE fix fbaec56):
      // RevenueBuild owns the GROWTH settings; DefRev owns the EXISTING BOOK + start_mode.
      // Send ONLY the 7 RevenueBuild-owned fields — no ...rawData.cqSettings spread. Every
      // omitted column (opening_arr, opening_bs_balance, existing_total_acv,
      // existing_monthly_rev, existing_arr, start_mode) is preserved by the server's
      // COALESCE-on-omit (all now undefined→NULL guarded server-side). start_mode is OMITTED
      // deliberately: RevenueBuild has no start_mode control (it only carries DefRev's value),
      // so omitting → COALESCE preserves DefRev's start_mode (strictly safer than carrying a
      // possibly-stale value). Allowlist > denylist: a future CQ column cannot leak in.
      const payload = {
        enabled: cqEnabled,
        growth_type: cqGrowthType,
        growth_mode: cqGrowthMode,
        growth_inputs: gi,
        renewal_mode: cqRenewalMode,
        churn_rate: cqChurnRate,
        manual_renewals: cqManualRenewals,
        /* The operator's ANSWER, not a description of the data. RevenueBuild owns this
           control, so it belongs in this tab's allowlist. The server validates it and
           refuses anything that is not one of the two values. */
        growth_period: cqGrowthPeriod,
      };
      cqStamp.adopt(await updateCompanyQuotaSettings(companyId, cqStamp.withStamp(payload)));
      setCqConflict(null);
      cqForceRef.current = new Set(Object.keys(payload)); // W-031 (C): the refetch re-seeds the fields just saved
      markStale();
      await ensureFresh();
    } catch (err) {
      console.error('Failed to save CQ settings:', err);
      /* Batch 1 (2026-11-03, L3645): EVERY refusal is shown beside Save -- the 409 stamp clash as before, and any other refusal
         (a server 400, a failed request) with its own text. It used to reach only the console. */
      const msg = stampConflictMessage(err) || ('Not saved: ' + String(err?.response?.data?.error || err?.message || 'unknown error'));
      setCqConflict({ at: 'save', msg });
    }
    finally { setCqSaving(false); }
  }, [companyId, cqEnabled, cqGrowthType, cqGrowthMode, cqGrowthInputs, cqGrowthPeriod, cqRenewalMode, cqChurnRate, cqManualRenewals, rawData?.cqSettings, markStale, ensureFresh, cqStamp]);

  /* ★ G3 — THIS TOGGLE NOW PERSISTS, LIKE ITS AE SIBLING. It used to be
   * `setCqEnabled(newVal)` and nothing else: `async` but awaiting nothing and calling no
   * API, so the flip lived in local state and was lost on navigation unless the operator
   * separately found and clicked "Save Settings". Two identical-looking controls on one
   * page, one durable and one not.
   * ⛔ SINGLE-FIELD PAYLOAD, exactly as toggleAeQuotaEnabled does — never a spread of
   * rawData.cqSettings. Every omitted column is preserved by the server's COALESCE-on-omit,
   * so { enabled } cannot null the DefRev-owned existing-book fields (§5's allowlist rule).
   * ⓘ FAILURE PATH: OPTIMISTIC SET, REVERT ON ERROR. The button must feel immediate, so the
   * state flips first; if the PUT rejects, it flips BACK and says so, rather than leaving the
   * UI claiming a state the database does not hold. Await-then-set would be simpler but
   * would make the toggle feel dead for the round trip. */
  const toggleCqEnabled = useCallback(async (newVal) => {
    if (!newVal) {
      const hasData = cqOpeningArr > 0 || Object.values(cqGrowthInputs).some(v => (parseFloat(v) || 0) !== 0);
      if (hasData) { setShowCqDisableWarning(true); return; }   // guard still aborts BEFORE any write
    }
    setCqEnabled(newVal);
    try {
      cqStamp.adopt(await updateCompanyQuotaSettings(companyId, cqStamp.withStamp({ enabled: newVal })));
      setCqConflict(null);
      markStale();
      await ensureFresh();
    } catch (err) {
      console.error('Failed to save CQ enabled:', err);
      { const msg = stampConflictMessage(err); if (msg) setCqConflict({ at: 'toggle', msg }); }
      setCqEnabled(!newVal);   // the DB did not take it — do not leave the UI lying
    }
  }, [companyId, cqOpeningArr, cqGrowthInputs, markStale, ensureFresh, cqStamp]);

  /* ★ …AND SO DOES THE CONFIRM ARM. The warning dialog is the ONLY way to disable a CQ that
   * has data, so leaving this one local-only would have persisted the toggle in exactly the
   * case that does not matter and not in the case that does. */
  const confirmCqDisable = useCallback(async () => {
    setShowCqDisableWarning(false);
    setCqEnabled(false);
    try {
      cqStamp.adopt(await updateCompanyQuotaSettings(companyId, cqStamp.withStamp({ enabled: false })));
      setCqConflict(null);
      markStale();
      await ensureFresh();
    } catch (err) {
      console.error('Failed to save CQ enabled:', err);
      { const msg = stampConflictMessage(err); if (msg) setCqConflict({ at: 'toggle', msg }); }
      setCqEnabled(true);
    }
  }, [companyId, markStale, ensureFresh, cqStamp]);

  // ═══ MISC REVENUE LOCAL STATE ═══
  const [miscEdits, setMiscEdits] = useState({}); // lineId → local edits object
  const [miscSaving, setMiscSaving] = useState({}); // lineId → true while saving
  const [miscSaved, setMiscSaved] = useState({}); // lineId → true for "Saved" indicator
  const [miscDisableWarning, setMiscDisableWarning] = useState(null); // lineId to show warning for
  const [miscSaveError, setMiscSaveError] = useState({}); // W-009 (C3/C4): lineId -> the empty-name block or a server refusal, shown beside Save
  const [miscRouteError, setMiscRouteError] = useState({}); // 5e-3: lineId → 409 target_already_routed message
  /* W-089 (Brian, 10-01; rule 92): lineId -> true for a line "+ Add Revenue Line" created in THIS session whose routing
     has not been chosen. Add POSTs at once with target NULL (measured), and a stored NULL is a WORKING answer --
     "Create new IS line", whose leaf the builder now owns -- so a NEW line is not allowed to look decided: its select
     starts blank, its caption says Required, and its Save is refused until a choice is made. */
  const [miscRouteUnanswered, setMiscRouteUnanswered] = useState({});

  // Sync local edits from context when lines change
  useEffect(() => {
    if (!ctxMiscLines?.length) return;
    setMiscEdits(prev => {
      const next = {};
      ctxMiscLines.forEach(l => {
        next[l.id] = prev[l.id] || {
          name: l.name,
          growth_type: l.growth_type || 'flat',
          growth_mode: l.growth_mode || 'compounding',
          growth_period: l.growth_period || 'annual',
          monthly_amounts: l.monthly_amounts || {},
          include_in_arr: !!l.include_in_arr,
          // 5e-3: '' = "Create new IS line" (Path B); a key = "Route to existing"
          // (Path A). The save sends this present (''→server NULLs it; key→routes).
          target_gl_line_key: l.target_gl_line_key || '',
        };
      });
      return next;
    });
  }, [ctxMiscLines]);

  const getMiscEdit = useCallback((lineId) => miscEdits[lineId] || {}, [miscEdits]);
  const setMiscField = useCallback((lineId, field, value) => {
    setMiscEdits(prev => ({ ...prev, [lineId]: { ...prev[lineId], [field]: value } }));
  }, []);

  const saveMiscLine = useCallback(async (lineId) => {
    const edit = miscEdits[lineId];
    if (!edit) return;
    /* W-009 (C3): a line must have a name (Brian: "it has to have some type of name"). Blocked here,
       before the round trip; the server refuses it too (validateMiscName). */
    if (!String(edit.name || '').trim()) {
      setMiscSaveError(prev => ({ ...prev, [lineId]: 'Give this revenue line a name before saving.' }));
      return;
    }
    if (miscRouteUnanswered[lineId]) {   // W-089: a new line's routing is required before its first save
      setMiscSaveError(prev => ({ ...prev, [lineId]: 'Choose where this line appears on the income statement before saving.' }));
      return;
    }
    setMiscSaving(prev => ({ ...prev, [lineId]: true }));
    try {
      await updateMiscRevenueLine(companyId, lineId, edit);
      setMiscRouteError(prev => ({ ...prev, [lineId]: null }));
      setMiscSaveError(prev => ({ ...prev, [lineId]: null }));
      markStale();
      await ensureFresh();
      setMiscSaved(prev => ({ ...prev, [lineId]: true }));
      setTimeout(() => setMiscSaved(prev => ({ ...prev, [lineId]: false })), 2000);
    } catch (err) {
      // 5e-3: server uniqueness guard — another line already routes to this key.
      // Keep the choice open (don't clear the edit) and surface it inline.
      if (err?.response?.data?.error === 'target_already_routed') {
        setMiscRouteError(prev => ({ ...prev, [lineId]: 'That IS line is already targeted by another misc revenue line — pick a different line (or set the other one back to "Create new IS line").' }));
      } else {
        /* W-009 (C4): a refusal now reaches the operator. It used to reach only the console, so a
           400 from the server's validators (or the old Postgres 500 on a long name) left the
           operator with a Save that silently did nothing. */
        console.error('Failed to save misc line:', err);
        const msg = err?.response?.data?.error || err?.message || 'Save failed.';
        setMiscSaveError(prev => ({ ...prev, [lineId]: String(msg) }));
      }
    }
    finally { setMiscSaving(prev => ({ ...prev, [lineId]: false })); }
  }, [companyId, miscEdits, miscRouteUnanswered, markStale, ensureFresh]);

  // ═══ IMPLEMENTATION REVENUE LOCAL STATE ═══
  const [implEdits, setImplEdits] = useState({}); // lineId → local edits object
  const [implSaving, setImplSaving] = useState({}); // lineId → true while saving
  const [implSaved, setImplSaved] = useState({}); // lineId → true for "Saved" indicator
  const [implDisableWarning, setImplDisableWarning] = useState(null);
  /* ★ PART D — lineId → the server's refusal, surfaced inline. Mirrors miscRouteError exactly;
     ⛔ NOT a fourth notice renderer — it reuses the misc route-error div's shape (measured:
     three hand-rolled notice renderers already exist, 09-11). */
  const [implRouteError, setImplRouteError] = useState({});
  /* W-023: a refusal the route slot does not cover -- a server error, a network failure, the take-rate check -- is shown BESIDE
     SAVE, in misc's own idiom (miscSaveError). The form keeps the edits either way. */
  const [implSaveError, setImplSaveError] = useState({});
  /* W-007: the Take Rate box's text as typed, so "12." and "12.5" can be typed and a blank stays blank (a number can hold neither).
     Absent => the box shows the stored take rate. */
  const [implTakeText, setImplTakeText] = useState({});
  // 2026-10-27: the "% of new ACV" box's text as typed (the take-rate box's shape). Absent => the box shows the stored percentage.
  const [implPctText, setImplPctText] = useState({});

  // Sync local edits from context when lines change
  useEffect(() => {
    if (!ctxImplLines?.length) { setImplEdits({}); return; }
    setImplEdits(prev => {
      const next = {};
      ctxImplLines.forEach(l => {
        next[l.id] = prev[l.id] || {
          source_type: l.source_type || 'standalone',
          source_id: l.source_id || null,
          name: l.name || 'Implementation',
          enabled: l.enabled !== false,
          avg_price: parseFloat(l.avg_price) || 0,
          impl_fee: parseFloat(l.impl_fee) || 0,
          /*  N-B  THE SAME COERCION LIVED HERE TOO, and leaving it would have been WORSE
             than leaving the engine alone: the engine would compute 0 while the editor
             showed 100%. One expression, mirroring the engine's normalisation exactly. */
          take_rate: (l.take_rate === null || l.take_rate === undefined || l.take_rate === '')
            ? 1 : (Number.isFinite(Number(l.take_rate)) ? Number(l.take_rate) : 1),
          recognition_mode: l.recognition_mode || 'immediate',
          standalone_amounts: l.standalone_amounts || {},
          /* ★ PART D — THE TWO PART-A COLUMNS, now edited here.
             ⛔ `?? ''` and `?? ''` deliberately, NOT `|| ''`: `length_months` 0 is a value the
             operator can type and the guard must REFUSE, so it must survive the sync rather
             than being coerced to blank. And target_gl_line_key NULL is the UNANSWERED state,
             which is meaningfully different from the sentinel — see the three states below. */
          target_gl_line_key: l.target_gl_line_key ?? null,
          length_months: l.length_months ?? null,
          /* 2026-10-27 -- THE FEE BASIS ("% of new ACV"; utils/implBasis.js owns the vocabulary). Drafted here, and SENT ONLY WHEN THE
             DRAFT DIFFERS FROM THE STORED VALUE (saveImplLine), so an untouched save body is unchanged. NULL stays NULL: the select
             shows it BLANK and Save is refused until a basis is chosen (Brian Q-C). The percentage is a fraction (0.2 = 20%). */
          impl_basis: l.impl_basis ?? null,
          pct_of_new_acv: storedPctOf(l),
        };
      });
      return next;
    });
  }, [ctxImplLines]);

  const getImplEdit = useCallback((lineId) => implEdits[lineId] || {}, [implEdits]);
  const setImplField = useCallback((lineId, field, value) => {
    setImplEdits(prev => ({ ...prev, [lineId]: { ...prev[lineId], [field]: value } }));
  }, []);

  const saveImplLine = useCallback(async (lineId) => {
    const edit = implEdits[lineId];
    if (!edit) return;
    /* 2026-10-27 -- THE FEE BASIS (Brian Q-C): a line's basis must be CHOSEN -- blank is not an answer. NOTE: no implementation_lines row
       existed on any company when this shipped (0 rows, measured 10-27), so every blank basis this page shows is a NEW, unanswered
       line; the engine keeps reading NULL as per client, and a new line's avg price is 0, so a blank line books 0 -- no silent revenue. */
    const refuse = msg => setImplSaveError(prev => ({ ...prev, [lineId]: msg }));
    const pctMode = isPctNewAcvBasis(edit.impl_basis);
    if (!pctMode && edit.impl_basis !== IMPL_BASIS_PER_CLIENT) { refuse(IMPL_BASIS_REQUIRED_MESSAGE); return; }
    if (pctMode) {
      // the % box: a blank / out-of-range / 3-decimal typing is refused here; the percentage is required (0 is an answer)
      if (implPctText[lineId] !== undefined) { const pr = parsePctNewAcvText(implPctText[lineId]); if (pr.error) { refuse(pr.error); return; } }
      if (edit.pct_of_new_acv === null || edit.pct_of_new_acv === undefined) { refuse(PCT_NEW_ACV_REQUIRED_MESSAGE); return; }
    }
    // W-007: a blank or out-of-range take rate is refused here, beside Save (the server refuses it too). Per-client mode only: in %
    // mode the Take Rate box is hidden and the take rate is not applied (Q-A), so a hidden draft must not block the save.
    if (!pctMode && implTakeText[lineId] !== undefined) {
      const tr = parseTakeRateText(implTakeText[lineId]);
      if (tr.error) { setImplSaveError(prev => ({ ...prev, [lineId]: tr.error })); return; }
    }
    /* the body: today's allowlist exactly, + impl_basis / pct_of_new_acv ONLY when the draft differs from the stored value -- so an
       untouched save sends what it always sent (the server keeps an omitted field: COALESCE). */
    const storedLine = (ctxImplLines || []).find(l => l.id === lineId) || {};
    const { impl_basis: draftBasis, pct_of_new_acv: draftPct, ...body } = edit;
    if ((draftBasis ?? null) !== (storedLine.impl_basis ?? null)) body.impl_basis = draftBasis;
    if (draftPct !== null && draftPct !== undefined && draftPct !== storedPctOf(storedLine)) body.pct_of_new_acv = draftPct;
    setImplSaveError(prev => ({ ...prev, [lineId]: null }));
    setImplSaving(prev => ({ ...prev, [lineId]: true }));
    try {
      await updateImplementationLine(companyId, lineId, body);
      setImplRouteError(prev => ({ ...prev, [lineId]: null }));
      markStale();
      await ensureFresh();
      setImplSaved(prev => ({ ...prev, [lineId]: true }));
      setTimeout(() => setImplSaved(prev => ({ ...prev, [lineId]: false })), 2000);
    } catch (err) {
      /* ★ PART D — the server guard's refusals, surfaced INLINE rather than swallowed.
         Keep the operator's choice open (⛔ do not clear the edit) so they can correct it.
         ⓘ 409 is the cross-line uniqueness clash (misc's own error code, reused verbatim);
         400 is the routing/length validator, whose message already names the problem. */
      const code = err?.response?.data?.error;
      // 2026-10-27: the fee-basis refusals (step 1's named codes) -> plain English beside Save; the draft is kept
      const basisMsg = err?.response?.status === 400 ? implBasisErrorMessage(err?.response?.data?.code) : null;
      if (basisMsg) {
        setImplSaveError(prev => ({ ...prev, [lineId]: basisMsg }));
      } else if (code === 'target_already_routed') {
        setImplRouteError(prev => ({ ...prev, [lineId]: 'That IS line is already targeted by another implementation line — pick a different line (or set the other one back to "Keep a separate forecast line").' }));
      } else if (err?.response?.status === 400 && code) {
        setImplRouteError(prev => ({ ...prev, [lineId]: code }));
      } else {
        // W-023: a server error or a network failure is SHOWN beside Save too -- it used to reach only the console.
        console.error('Failed to save impl line:', err);
        setImplSaveError(prev => ({ ...prev, [lineId]: String(err?.response?.data?.error || err?.message || 'Save failed.') }));
      }
    }
    finally { setImplSaving(prev => ({ ...prev, [lineId]: false })); }
  }, [companyId, implEdits, implTakeText, implPctText, ctxImplLines, markStale, ensureFresh]);

  /* ★ ITEM: the two singleton revenue modules are named for the operator by the INCOME
     STATEMENT LINE they forecast, falling back to the engine name while unmapped.
     ⛔ The tab IDs stay `ae_quota` / `company_quota` — only the rendered text moves. */
  const aeLineLabel = moduleLineLabel(rawData?.glMappings, rawData?.revMappings, 'ae_quota', 'Account Executive Quota');
  const cqLineLabel = moduleLineLabel(rawData?.glMappings, rawData?.revMappings, 'company_quota', 'Company Quota');

  // Helper: get source name from source_type + source_id
  const getImplSourceName = useCallback((sourceType, sourceId) => {
    if (sourceType === 'ae_quota') return aeLineLabel;
    if (sourceType === 'company_quota') return cqLineLabel;
    if (sourceType === 'misc_rev') {
      const ml = (ctxMiscLines || []).find(l => l.id === sourceId);
      return ml ? ml.name : `Misc #${sourceId}`;
    }
    return 'Standalone';
  }, [ctxMiscLines, aeLineLabel, cqLineLabel]);

  // Track which sources are already used by other impl lines
  const usedImplSources = useMemo(() => {
    const used = {};
    (ctxImplLines || []).forEach(l => {
      const key = l.source_type === 'misc_rev' ? `misc_rev_${l.source_id}` : l.source_type;
      used[key] = l.id;
    });
    return used;
  }, [ctxImplLines]);

  const loadSalesReps = useCallback(async () => {
    setAeLoading(true);
    setRepsError(null);
    try {
      const [res, settingsRes] = await Promise.all([
        getEmployeesWithActuals(companyId),
        getCompanySettings(companyId),
      ]);
      setCommissionDelay(parseInt(settingsRes.data?.commission_delay_months) || 0);
      const seen = new Set();
      const lastM = allMonths.length ? allMonths[allMonths.length - 1] : "";
      const reps = res.data.filter(e => {
        if (!e.is_sales_rep) return false;
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        // Active check: has salary > 0 or no term date, or term date is after cutoff (in forecast range)
        const termDate = e.end_date || e.term_date || null;
        const isActive = !termDate || (typeof termDate === 'string' && termDate.slice(0,7) > (cutoff || ""));
        return isActive;
      }).map(e => {
        const startDate = e.start_date ? (typeof e.start_date === 'string' ? e.start_date.slice(0,10) : null) : null;
        // Infer termDate from monthlyData (same as ModelProvider)
        const md = e.monthlyData || {};
        const activeMs = allMonths.filter(m => (md[m] || 0) > 0);
        const lastActive = activeMs.length ? activeMs[activeMs.length - 1] : null;
        const hasTerminated = lastActive && lastActive < lastM;
        let termDate = null;
        if (hasTerminated && lastActive) {
          termDate = lastActive + "-28";
        } else if (e.end_date && typeof e.end_date === 'string') {
          termDate = e.end_date.slice(0,10);
        }
        const qmf = e.quota_monthly_factors ? (typeof e.quota_monthly_factors === 'string' ? JSON.parse(e.quota_monthly_factors) : e.quota_monthly_factors) : {};
        return {
          id: e.id,
          name: e.name,
          title: e.title || "",
          department: e.department || "",
          startDate,
          termDate,
          annual_quota: parseFloat(e.annual_quota) || 0,
          /* ★ RAMP STAYS PER REP — a new hire's ramp, owned and edited on the PAYROLL tab.
           * It is carried through this page's save UNTOUCHED, because the PATCH writer is a
           * WHOLE-OBJECT REPLACE and anything omitted is WIPED.
           * ⛔ `distribution` / `seasonal_factors` are RETIRED here — the pattern is
           *   company-wide now and lives on ae_quota_settings.quota_distribution. */
          ramp_months: qmf.ramp_months || 0,
          ramp_schedule: qmf.ramp_schedule || [],
        };
      });
      setSalesReps(reps);
    } catch (err) {
      console.error('Failed to load sales reps:', err);
      setRepsError(err?.response?.data?.error || err?.message || "unknown error");
    } finally {
      repsLoadedFor.current = companyId;
      setAeLoading(false);
    }
  }, [companyId, allMonths, cutoff]);

  /* ★★ SEED THE CONTROL ONCE PER TENANT — ⛔ NEVER ON EVERY CHANGE OF THE CACHED VALUE.
   *
   * ⚠⚠ THIS SHAPE REPLACES A DATA-LOSS PATH, AND THE OLD ONE IS QUOTED SO THE DIFFERENCE IS
   * LEGIBLE:
   *     const storedQuotaDistribution = rawData?.aeqSettings?.quota_distribution;
   *     useEffect(() => {
   *       if (aeDirty) return;
   *       setQuotaPattern(normalizeQuotaPattern(storedQuotaDistribution));
   *     }, [storedQuotaDistribution, aeDirty]);
   *
   * `saveAeQuota` ends `markStale(); setAeDirty(false);`. **`markStale()` only FLAGS
   * staleness — it does NOT refetch.** So `rawData.aeqSettings` still held the PRE-SAVE
   * value, and the instant `aeDirty` flipped false the effect overwrote the freshly-saved
   * pattern with the stale one: the control reverted to Straight Line while the database
   * held the seasonal pattern (measured live on cid=36 — server "seasonal"/36 factors,
   * control "Straight Line").
   * ⇒ ★★ AND THE CONSEQUENCE WAS DATA LOSS, NOT COSMETICS: the operator sees the revert,
   *   concludes the save failed, presses Save again — and the client then writes the
   *   SENTINEL over the pattern they just saved.
   * ⓘ The `if (aeDirty) return` guard protected a half-typed edit and did NOTHING for the
   *   one instant when the cache is GUARANTEED stale.
   *
   * ⇒ THE FIX IS TO TAKE THE CACHE OUT OF THE WRITE-BACK PATH, not to work around its
   *   staleness: seed once when the tenant's stored value FIRST arrives, and thereafter let
   *   the save set the local state FROM THE SERVER'S OWN RESPONSE (see saveAeQuota).
   *   That is §12.5 rule 27 — READ THE OWNER, NEVER A SIBLING'S CACHE. */
  const seededForCompany = useRef(null);
  useEffect(() => {
    if (!rawData) return;                      // wait for the first fetch
    if (seededForCompany.current === companyId) return;   // already seeded for this tenant
    seededForCompany.current = companyId;
    setQuotaPattern(normalizeQuotaPattern(rawData?.aeqSettings?.quota_distribution));
  }, [companyId, rawData]);

  // Load sales reps when switching to AE Quota tab
  const switchView = useCallback((v) => {
    setView(v);
    if (v === "ae_quota" && salesReps.length === 0 && repsLoadedFor.current !== companyId) loadSalesReps();
  }, [salesReps.length, loadSalesReps, companyId]);

  // Auto-load sales reps when AE Quota is the active/effective view. switchView lazy-loads
  // reps on a manual AE click, but the legacy=false default-flip selects AE via setView (the
  // effect above) — bypassing that load, so reps stayed empty until a manual CQ→AE click.
  // Keyed on effectiveView so the auto-selected AE tab loads its reps too. The length===0 +
  // !aeLoading guards dedup against switchView's inline load → cid=1 byte-identical (no
  // double-fetch; loadSalesReps sets aeLoading=true synchronously at its start).
  useEffect(() => {
    if (effectiveView === "ae_quota" && salesReps.length === 0 && !aeLoading && repsLoadedFor.current !== companyId) loadSalesReps();
  }, [effectiveView, salesReps.length, aeLoading, loadSalesReps, companyId]);

  // Compute monthly new ACV for each rep
  const aeData = useMemo(() => {
    return salesReps.map(rep => {
      const months = {};
      forecastMonths.forEach(m => {
        // ★ THE ONE OWNER — the same function both engine sites call.
        const monthlyAmt = monthlyQuotaAmount(rep.annual_quota, quotaPattern, m);

        // Check if employee has started yet
        let rampPct = 1;
        if (rep.startDate) {
          const startM = rep.startDate.slice(0, 7); // YYYY-MM
          if (m < startM) {
            rampPct = 0; // not yet hired
          } else if (rep.ramp_schedule.length > 0) {
            const [sy, sm] = rep.startDate.split("-").map(Number);
            const [my, mm] = m.split("-").map(Number);
            const monthsSinceHire = (my - sy) * 12 + (mm - sm);
            if (monthsSinceHire < rep.ramp_schedule.length) {
              rampPct = (rep.ramp_schedule[monthsSinceHire] || 0) / 100;
            }
            // else rampPct stays 1 (fully ramped)
          }
        }

        // Zero after termination
        const termM = rep.termDate ? rep.termDate.slice(0, 7) : null;
        if (termM && m > termM) {
          months[m] = 0;
        } else {
          months[m] = r2(monthlyAmt * rampPct);
        }
      });
      return { ...rep, months };
    });
  }, [salesReps, forecastMonths, quotaPattern]);

  // Update a rep field locally

  // Update ONE month of the COMPANY-WIDE pattern.
  const updateQuotaFactor = useCallback((month, value) => {
    setQuotaPattern(p => ({ ...p, factors: { ...p.factors, [month]: value } }));
    setAeDirty(true);
  }, []);

  /* W-080 (C), 2026-09-28: copy one calendar year's twelve factors onto the next (Jan->Jan ...
   * Dec->Dec). The operator presses it; nothing is copied automatically. A source month with no
   * value copies as 0, so the target year becomes PRESENT and its running sum shows what is
   * left to enter. The obvious way in when a longer window adds a year with no values. */
  const copyQuotaYear = useCallback((fromYear, toYear) => {
    setQuotaPattern(p => {
      const next = { ...p.factors };
      for (let i = 1; i <= 12; i++) {
        const mm = String(i).padStart(2, "0");
        const v = p.factors[`${fromYear}-${mm}`];
        next[`${toYear}-${mm}`] = v !== undefined ? v : 0;
      }
      return { ...p, factors: next };
    });
    setAeDirty(true);
  }, []);

  /* Switch the company between the two distributions.
   * ★★ THE SEED IS PER CALENDAR YEAR, AND IT REPLACES THE RETIRED PER-REP SEEDER, which
   * was broken INDEPENDENTLY of the calendar-year defect: it indexed `forecastMonths` by
   * POSITION and applied `r2` — a MONEY rounder, two decimals — to a FRACTION, producing
   * 99.63% at twelve months, 74.97% at nine and 139.63% at seventeen. Its OWN validator
   * blocked every one, which is why the seasonal path could not be saved AT ALL.
   * ⇒ `seedEvenYear` carries the remainder at full precision and lands it on December, so
   *   every seeded year sums to exactly 100.00% at ANY window length. */
  const setQuotaMode = useCallback((mode) => {
    setAeDirty(true);
    setQuotaPattern(p => {
      if (mode !== "seasonal") return { mode: "straight_line", factors: {} };
      if (Object.keys(p.factors || {}).length > 0) return { ...p, mode: "seasonal" };
      const seeded = {};
      quotaGridYears(cutoff, allMonths).forEach(y => Object.assign(seeded, seedEvenYear(y)));
      return { mode: "seasonal", factors: seeded };
    });
  }, [allMonths, cutoff]);

  /* Save the COMPANY pattern + the reps.
   * ★★ THE VALIDATION IS PER CALENDAR YEAR, ⛔ NEVER ACROSS THE WINDOW. That is the whole
   * defect being fixed: the retired gate summed `forecastMonths`, so a 17-month window
   * compressed ONE year's quota into seventeen months (measured: $600,000 booked where
   * $850,000 was correct, −29.4%).
   * ⓘ It calls the SAME `validateQuotaPattern` the server re-implements, and the build's
   *   verify asserts the two agree case-for-case (THE WALL forbids sharing the code). */
  const saveAeQuota = useCallback(async () => {
    /* ★ PRUNE TO THE GRID BEFORE VALIDATING OR SENDING.
     * ⛔ THIS IS WHAT KEEPS THE THREE IN AGREEMENT: the grid shows the seam year onward, so
     *   the client validator must not demand 100% for a year the grid no longer renders, and
     *   the DEPLOYED server validator must not be handed one it cannot be repaired to.
     * ⓘ It drops only years the operator can no longer see, and those are INERT — the engine
     *   reads no month at or before the cutoff. */
    const pruned = quotaPattern.mode === "seasonal"
      ? { mode: "seasonal", factors: pruneToGrid(quotaPattern.factors, cutoff, allMonths) }
      : { mode: "straight_line" };
    /* W-080 (B), 2026-09-28: IN SEASONAL MODE EVERY GRID YEAR MUST BE PRESENT. validateQuotaPattern
     * checks only the years the map CARRIES, so a pattern missing a whole forecast year used to
     * save -- while the grid showed that year red ("Must sum to 100.00%") -- and the engine booked
     * that year straight line. The years are quotaGridYears, the grid's own rule (every one holds
     * forecast months); a year present but incomplete or off 100% is already named by
     * validateQuotaPattern. CLIENT-SIDE ONLY: the server does not know the forecast window, so a
     * direct API call can still store a map missing a year (filed). */
    const presentYears = pruned.mode === "seasonal" ? factorsByYear(pruned.factors) : {};
    const gridYears = quotaGridYears(cutoff, allMonths);
    const missingYears = pruned.mode === "seasonal" ? gridYears.filter(y => !presentYears[y]) : [];
    const errs = [
      ...validateQuotaPattern(pruned),
      ...missingYears.map(y => {
        const prev = gridYears[gridYears.indexOf(y) - 1];
        return { year: y, code: "missing_year",
          message: `${y} has no seasonal pattern \u2014 enter one${prev ? ` (or copy ${prev}'s)` : ""} before saving.` };
      }),
    ];
    const byYear = {};
    errs.forEach(e => { if (e.year) byYear[e.year] = e.message; });
    setAeErrors(byYear);
    if (errs.length > 0) {
      alert("The quota pattern is not valid:\n\n" + errs.map(e => "• " + e.message).join("\n") +
            "\n\nQuota is a calendar-year concept — each year books one annual quota.");
      return;
    }

    setAeSaving(true);
    try {
      await updateCompanySettings(companyId, { commission_delay_months: commissionDelay });
      /* ★ THE COMPANY-WIDE PATTERN. `{ mode: "straight_line" }` is the SENTINEL — non-null
       * so the server's COALESCE can STORE it. ⛔ Sending null/undefined would mean
       * "preserve", so clearing would be unreachable (the Part-D COALESCE trap). */
      /* ★★ ADOPT THE SERVER'S OWN RESPONSE — the PUT does `RETURNING *`, so `res.data` IS
       * the stored row. Reading it back is READ-THE-OWNER; it also means a server-side
       * normalisation could never diverge silently from what the screen shows.
       * ⛔ ON ITS OWN THIS WOULD NOT HAVE FIXED ANYTHING — the retired effect re-ran on
       *   `setAeDirty(false)` a few lines below and overwrote it from the stale cache. It
       *   works only BECAUSE that effect no longer clobbers (see the seed-once effect). */
      const saved = await updateAEQuotaSettings(companyId, aeStamp.withStamp({ quota_distribution: pruned }));
      aeStamp.adopt(saved);
      setAeConflict(null);
      setQuotaPattern(normalizeQuotaPattern(saved?.data?.quota_distribution ?? pruned));
      for (const rep of salesReps) {
        /* ⛔⛔ THE PATCH WRITER IS A WHOLE-OBJECT REPLACE with a present-key-only SET list
         * (server.js), so whatever this object omits is WIPED from the stored column.
         * ★ `ramp_months` and `ramp_schedule` are PER-REP data owned by the PAYROLL tab and
         *   MUST survive this save untouched — they are the only reason this object still
         *   exists. `distribution` and `seasonal_factors` are deliberately DROPPED, which is
         *   what retires the per-rep pattern. ⛔ Do not "tidy" this into a spread. */
        const qmf = {
          ramp_months: rep.ramp_months,
          ramp_schedule: rep.ramp_schedule,
        };
        await patchEmployee(companyId, rep.id, {
          annual_quota: rep.annual_quota,
          quota_monthly_factors: qmf,
        });
      }
      markStale();
      setAeDirty(false);
    } catch (err) {
      console.error('Failed to save AE quota:', err);
      const msg = stampConflictMessage(err); if (msg) setAeConflict({ at: 'save', msg });
    } finally {
      setAeSaving(false);
    }
  }, [companyId, salesReps, commissionDelay, markStale, quotaPattern, cutoff, allMonths, aeStamp]);

  /* ★ CRASHROOT STEP 2 — this page now reads `modelState`, the provider's single
     terminal-exit answer. Until this build it read NEITHER modelState NOR
     onboardingGap, so a computed-but-HONESTLY-EMPTY tenant (no forecast horizon, or
     no model window) and a THROWN fetch alike cleared this page's own `loading` with
     allMonths===[] — and it spun FOREVER. Both hangs were reproduced on the pre-fix
     tree: diffs/_crashroot2-verifyA-pre.txt.
     'empty' REUSES the existing OnboardingIncomplete JSX (the gap object still names
     the copy); 'error' is the SHARED ModelLoadError added in step 1 — one
     implementation, imported, never an error <div> per page (Principle 0).
     (Group 3 F8.6, 2026-10-02) This page's own `loading` flag is DELETED with the legacy loader that set it;
     the guard below waits on the provider's modelState and the window, like its sibling pages. */
  if (modelState === 'error') return <ModelLoadError message={modelError} />;
  if (modelState === 'empty') return <OnboardingIncomplete gap={onboardingGap} />;

  if (modelState === 'loading' || !allMonths.length) return <div style={{padding:"60px",textAlign:"center",color:"#8B8FA3",fontFamily:"'DM Sans',sans-serif"}}>Loading revenue data...</div>;

  const pill = a => ({padding:"7px 16px",borderRadius:"8px",fontSize:"12px",fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",border:a?"none":"1px solid rgba(0,0,0,0.1)",background:a?"linear-gradient(135deg,#3D85C6,#5A5FC0)":"transparent",color:a?"#fff":"#6B7094",transition:"all 0.2s"});
  const SL = {position:"sticky",left:0,background:"#FFFFFF",borderRight:"1px solid rgba(0,0,0,0.07)",zIndex:10};

  return(
    <div style={{fontFamily:"'DM Sans',sans-serif",background:"#F5F6FA",minHeight:"100vh",padding:"24px"}}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet"/>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"24px"}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"8px"}}>
            <h1 style={{margin:0,fontSize:"22px",fontWeight:700,color:"#1A1C2E",letterSpacing:"-0.5px"}}>Revenue Build</h1>
            <span style={{fontSize:"10px",padding:"3px 10px",borderRadius:"6px",background:"rgba(61,133,198,0.12)",color:"#2A6DB0",fontWeight:700}}>SaaS ARR MODEL</span>
          </div>
          <div style={{display:"flex",gap:"16px",fontSize:"11px",color:"#8B8FA3"}}>
            {/* THE LENGTH CHECK COMES FIRST — the KPI treatment (b96e9d5), applied to the sibling
    this line was copy-pasted from. It used to open with getYear(...) and only then test
    the RESULT for null, which is a guard on the call that throws, i.e. no guard at all.
    The page gate above already stops an empty window reaching here; the line is now
    correct ON ITS OWN so it does not depend on a guard hundreds of lines away. Same
    output for a real window.
    THE RECEIVER HERE IS actualsMonths, NOT allMonths, so that is what is
    checked. Guarding allMonths would be a guard on an unrelated array: blind to the one
    frame that can actually reach this line (a non-empty window whose actuals slice is
    empty), and it would make the harness pass for the wrong reason. The outer
    `actualsMonths.length>0 &&` is kept — this guard is additive, not a replacement. */}
            {actualsMonths.length>0 && <span><span style={{color:"#A0A3B8"}}>{actualsMonths.length} mo actuals ({(()=>{if(!actualsMonths.length)return"—";const a=getYear(actualsMonths[0]);const b=getYear(actualsMonths[actualsMonths.length-1]);return a===b?String(a):`${a}–${b}`;})()})</span></span>}
            {forecastMonths.length>0 && <span style={{color:"#2A6DB0"}}>{forecastMonths.length} mo forecast ({forecastYearRangeLabel})</span>}
          </div>
        </div>
        <div style={{display:"flex",gap:"5px"}}>
          {[["ae_quota",aeLineLabel],["company_quota",cqLineLabel + (cqEnabled ? "" : " (off)")],["misc_rev","Misc Rev"],["impl","Implementation"]].map(([v,l])=><button key={v} onClick={()=>switchView(v)} style={pill(effectiveView===v)}>{l}</button>)}
        </div>
      </div>

      {/* ═══ AE QUOTA ═══ */}
      {effectiveView==="ae_quota"&&(()=>{
        if (aeLoading) return <div style={{padding:"60px",textAlign:"center",color:"#8B8FA3"}}>Loading sales reps...</div>;
        if (salesReps.length===0 && repsError) return <div data-reps-error="load" style={{padding:"60px",textAlign:"center",color:"#E07A5F",fontWeight:600}}>Could not load the sales reps: {repsError} <button onClick={loadSalesReps} style={{padding:"8px 14px",borderRadius:"8px",border:"1px solid rgba(0,0,0,0.12)",background:"transparent",color:"#6B7094",cursor:"pointer",fontSize:"11px",fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>Retry</button></div>;
        if (salesReps.length===0) return <div style={{padding:"60px",textAlign:"center",color:"#8B8FA3"}}>No sales reps found. Tag employees as sales reps on the Payroll page.</div>;

        // Totals
        const totalQuota = r2(salesReps.reduce((s,r)=>s+r.annual_quota,0));
        const totalsByMonth = {};
        forecastMonths.forEach(m => { totalsByMonth[m] = r2(aeData.reduce((s,r)=>s+(r.months[m]||0),0)); });
        const totalACV = r2(Object.values(totalsByMonth).reduce((s,v)=>s+v,0));

        return <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",padding:"24px"}}>
          {/* AE Quota Enable Toggle */}
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16,padding:"10px 14px",borderRadius:8,background:aeQuotaEnabled?"rgba(129,178,154,0.06)":"rgba(0,0,0,0.03)",border:"1px solid "+(aeQuotaEnabled?"rgba(129,178,154,0.15)":"rgba(0,0,0,0.08)")}}>
            <span style={{fontSize:12,fontWeight:700,color:aeQuotaEnabled?"#1A1C2E":"#8B8FA3"}}>Account Executive Quota Revenue</span>
            <ViewOnly canEdit={canEditModel}><button onClick={()=>toggleAeQuotaEnabled(!aeQuotaEnabled)} disabled={aeQuotaEnableSaving} style={{padding:"4px 14px",borderRadius:6,border:"none",background:aeQuotaEnabled?"#81B29A":"#D1D5DB",color:"#fff",fontSize:10,fontWeight:700,cursor:aeQuotaEnableSaving?"not-allowed":"pointer",opacity:aeQuotaEnableSaving?0.6:1}}>{aeQuotaEnabled?"Enabled":"Disabled"}</button></ViewOnly>
            {!aeQuotaEnabled && <span style={{fontSize:10,color:"#8B8FA3",fontStyle:"italic"}}>Revenue will not flow to IS/BS/CFS while disabled</span>}
            {aeConflict?.at === 'toggle' && <span data-stamp-conflict="ae-toggle" style={{marginLeft:10,fontSize:11,fontWeight:600,color:"#E07A5F"}}>{aeConflict.msg}</span>}
          </div>
          {showAeDisableWarning && <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.4)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowAeDisableWarning(false)}>
            <div style={{background:"#fff",borderRadius:12,padding:24,maxWidth:420,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
              <div style={{fontSize:14,fontWeight:700,color:"#1A1C2E",marginBottom:8}}>Disable Account Executive Quota Revenue?</div>
              <div style={{fontSize:12,color:"#6B7094",marginBottom:16}}>Disabling Account Executive Quota will remove its revenue from IS, BS, and CFS. Your data is preserved and can be re-enabled. Continue?</div>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <button onClick={()=>setShowAeDisableWarning(false)} style={{padding:"7px 16px",borderRadius:6,border:"1px solid #D1D5DB",background:"#fff",color:"#4B5563",fontSize:11,fontWeight:600,cursor:"pointer"}}>Cancel</button>
                <ViewOnly canEdit={canEditModel}><button onClick={confirmAeDisable} style={{padding:"7px 16px",borderRadius:6,border:"none",background:"#E07A5F",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}}>Disable</button></ViewOnly>
              </div>
            </div>
          </div>}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"20px"}}>
            <div>
              <div style={{fontSize:"16px",fontWeight:700,color:"#1A1C2E",marginBottom:"6px"}}>Account Executive Quota — {forecastYearRangeLabel} New ACV Forecast</div>
              <div style={{fontSize:"12px",color:"#8B8FA3"}}>Monthly new ACV by sales rep. Annual quota × distribution × ramp schedule.</div>
            </div>
            <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
              <button onClick={loadSalesReps} style={{padding:"8px 14px",borderRadius:"8px",border:"1px solid rgba(0,0,0,0.12)",background:"transparent",color:"#6B7094",cursor:"pointer",fontSize:"11px",fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>Refresh</button>
              {repsError && <span data-reps-error="refresh" style={{marginLeft:10,fontSize:11,fontWeight:600,color:"#E07A5F"}}>Could not reload the sales reps: {repsError}</span>}
              <ViewOnly canEdit={canEditModel}><button onClick={saveAeQuota} disabled={aeSaving} style={{padding:"8px 18px",borderRadius:"8px",border:"none",background:"linear-gradient(135deg,#3D85C6,#5A5FC0)",color:"#fff",cursor:aeSaving?"not-allowed":"pointer",fontSize:"11px",fontWeight:700,fontFamily:"'DM Sans',sans-serif",opacity:aeSaving?0.6:1}}>{aeSaving?"Saving...":"Save"}</button></ViewOnly>
              {aeConflict?.at === 'save' && <span data-stamp-conflict="ae-save" style={{marginLeft:10,fontSize:11,fontWeight:600,color:"#E07A5F"}}>{aeConflict.msg}</span>}
            </div>
          </div>

          {/* Summary cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"14px",marginBottom:"24px"}}>
            <div style={{padding:"14px 18px",borderRadius:"10px",background:"rgba(61,133,198,0.04)",border:"1px solid rgba(61,133,198,0.1)"}}>
              <div style={{fontSize:"10px",fontWeight:700,color:"#3D85C6",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"4px"}}>Sales Reps</div>
              <div style={{fontSize:"22px",fontWeight:700,color:"#1A1C2E",fontFamily:"'Space Mono',monospace"}}>{salesReps.length}</div>
            </div>
            <div style={{padding:"14px 18px",borderRadius:"10px",background:"rgba(129,178,154,0.06)",border:"1px solid rgba(129,178,154,0.15)"}}>
              <div style={{fontSize:"10px",fontWeight:700,color:"#81B29A",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"4px"}}>Total Annual Quota</div>
              <div style={{fontSize:"22px",fontWeight:700,color:"#81B29A",fontFamily:"'Space Mono',monospace",letterSpacing:"-1px"}}>{fmtK(totalQuota)}</div>
            </div>
            <div style={{padding:"14px 18px",borderRadius:"10px",background:"rgba(192,57,43,0.05)",border:"1px solid rgba(192,57,43,0.12)"}}>
              <div style={{fontSize:"10px",fontWeight:700,color:"#C0392B",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"4px"}}>Total {forecastYearRangeLabel} New ACV</div>
              <div style={{fontSize:"22px",fontWeight:700,color:"#C0392B",fontFamily:"'Space Mono',monospace",letterSpacing:"-1px"}}>{fmtK(totalACV)}</div>
            </div>
          </div>

          {/* Payment Lag control moved to the Compensation Cycle card (Payroll tab + onboarding
              Step 9), item #1 part 2. The same companies.commission_delay_months; engine read
              unchanged. This page still loads/round-trips the value but no longer edits it. */}

          {/* ★★ THE COMPANY-WIDE QUOTA DISTRIBUTION — ONE control for every rep. */}
          {(() => {
            const years = quotaGridYears(cutoff, allMonths);
            const seasonal = quotaPattern.mode === "seasonal";
            const fx = quotaPattern.factors || {};
            /* ★ THE COVERAGE WARNING IS DERIVED HERE, not projected from the engine.
             * §12.5 rule 42 forbids a computed refusal with no reader, and ModelProvider names
             * every projected key explicitly rather than spreading — so an engine-side channel
             * would have needed an edit there. This page owns the pattern AND the window, so it
             * can answer the question itself. ⇒ ModelProvider stays at ZERO edits.
             * ⓘ Reachable only when the window GROWS past a saved pattern (the filed
             *   "Extend Forecast End" module); the grid covers the whole window at save time. */
            const warn = seasonal
              ? forecastMonths.filter(m => fx[m] === undefined).map(m => ({ month: m,
                  message: `${m} has no seasonal percentage — that month falls back to annual quota / 12. Add ${m.slice(0,4)} to the pattern.` }))
              : [];
            return <ViewOnly canEdit={canEditModel}><div style={{marginBottom:"20px",padding:"14px 18px",borderRadius:"10px",background:"rgba(61,133,198,0.03)",border:"1px solid rgba(61,133,198,0.12)"}}>
              <div style={{display:"flex",alignItems:"center",gap:"12px",flexWrap:"wrap"}}>
                <span style={{fontSize:"11px",fontWeight:700,color:"#3D85C6",textTransform:"uppercase",letterSpacing:"0.5px"}}>Quota Distribution</span>
                <select value={quotaPattern.mode} onChange={e=>setQuotaMode(e.target.value)}
                  style={{padding:"6px 10px",borderRadius:"6px",border:"1px solid rgba(0,0,0,0.12)",fontSize:"12px",fontFamily:"'DM Sans',sans-serif",background:"#FFFFFF",cursor:"pointer"}}>
                  <option value="straight_line">Straight Line</option>
                  <option value="seasonal">Seasonal</option>
                </select>
                <span style={{fontSize:9,color:"#8B8FA3"}}>Applies to every sales rep. Straight Line books annual quota ÷ 12 each month.</span>
              </div>
              {warn.length>0&&<div style={{marginTop:8,fontSize:9,color:"#E07A5F",fontWeight:600}}>
                {warn.map(w=><div key={w.month}>{w.message}</div>)}
              </div>}
              {seasonal&&<div style={{marginTop:"14px",overflowX:"auto"}}>
                <div style={{fontSize:9,color:"#8B8FA3",marginBottom:6}}>
                  Each calendar year must sum to 100%. Closed months are shown so a partial year still reaches 100% — entering them does not change history.
                </div>
                {years.map((y, yi) => {
                  const ms = Array.from({length:12},(_,i)=>`${y}-${String(i+1).padStart(2,"0")}`);
                  const sum = ms.reduce((s,m)=>s+(parseFloat(fx[m])||0),0);
                  const bad = Math.abs(sum-1)>=0.0001;
                  return <div key={y} style={{marginBottom:"10px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"3px"}}>
                      <span style={{fontSize:10,fontWeight:700,color:"#1A1C2E"}}>{y}</span>
                      <span style={{fontSize:9,fontWeight:700,fontFamily:"'Space Mono',monospace",color:bad?"#E07A5F":"#81B29A"}}>({(sum*100).toFixed(2)}%)</span>
                      {bad&&<span style={{fontSize:9,color:"#E07A5F",fontWeight:600}}>Must sum to 100.00%</span>}
                      {aeErrors[y]&&<span style={{fontSize:9,color:"#E07A5F"}}>{aeErrors[y]}</span>}
                      {/* W-080 (C): copy the previous year's twelve months into this one -- operator-initiated. */}
                      {yi>0&&<button type="button" onClick={()=>copyQuotaYear(years[yi-1],y)} title={`Fill ${y}'s twelve months from ${years[yi-1]}'s pattern`}
                        style={{marginLeft:"auto",padding:"2px 8px",borderRadius:4,border:"1px solid #D1D5DB",background:"#fff",color:"#4B5563",fontSize:9,fontWeight:600,cursor:"pointer"}}>Copy {years[yi-1]}</button>}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(12,minmax(52px,1fr))",gap:"3px"}}>
                      {ms.map(m => {
                        const closed = !!(cutoff && m <= cutoff);
                        return <div key={m}>
                          <div style={{fontSize:8,color:closed?"#A0A3B8":"#6B7094",textAlign:"center",marginBottom:1}}>
                            {formatMonthLabel(m,"short")}{closed?" · closed":""}
                          </div>
                          <SeasonalPctInput value={fx[m]||0} onChange={v=>updateQuotaFactor(m,v)}
                            style={{width:"100%",padding:"3px 4px",borderRadius:"4px",border:bad?"1px solid rgba(224,122,95,0.4)":"1px solid rgba(61,133,198,0.2)",fontSize:"9px",fontFamily:"'Space Mono',monospace",textAlign:"right",background:closed?"rgba(0,0,0,0.03)":"rgba(61,133,198,0.04)"}}/>
                        </div>;
                      })}
                    </div>
                  </div>;
                })}
              </div>}
            </div></ViewOnly>;
          })()}

          {/* AE Grid */}
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px",minWidth:`${forecastMonths.length*80+460}px`}}>
              <thead>
                <tr style={{borderBottom:"2px solid rgba(0,0,0,0.08)"}}>
                  <th style={{...SL,padding:"10px 12px",textAlign:"left",fontSize:"10px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase",minWidth:"140px",background:"#FFFFFF"}}>Name</th>
                  <th style={{padding:"10px 8px",textAlign:"left",fontSize:"10px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase",minWidth:"100px"}}>Title</th>
                  <th style={{padding:"10px 8px",textAlign:"right",fontSize:"10px",fontWeight:600,color:"#81B29A",textTransform:"uppercase",minWidth:"120px"}}>Annual Quota</th>
                  <th style={{padding:"10px 8px",textAlign:"center",fontSize:"10px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase",minWidth:"80px"}}>Ramp</th>
                  {forecastMonths.map(m=><th key={m} style={{padding:"10px 4px",textAlign:"right",fontSize:"9px",fontWeight:600,color:"#1A1C2E",whiteSpace:"nowrap"}}>{formatMonthLabel(m, "shortYear")}</th>)}
                  <th style={{padding:"10px 8px",textAlign:"right",fontSize:"10px",fontWeight:700,color:"#1A1C2E",textTransform:"uppercase"}}>Total</th>
                </tr>
              </thead>
              <tbody>
                {aeData.map(rep => {
                  const repTotal = r2(forecastMonths.reduce((s,m)=>s+(rep.months[m]||0),0));
                  return <Fragment key={rep.id}>
                    <tr style={{borderBottom:"1px solid rgba(0,0,0,0.04)"}}>
                      <td style={{...SL,padding:"8px 12px",fontWeight:600,color:"#1A1C2E",fontSize:"12px",background:"#FFFFFF"}}>
                        <div style={{cursor:"pointer",color:"#3D85C6",borderBottom:"1px dashed rgba(61,133,198,0.3)",display:"inline"}} onClick={()=>{if(aeDirty)markStale();navigate("/payroll",{state:{editEmployeeId:rep.id}});}}>{rep.name}</div>
                      </td>
                      <td style={{padding:"8px 8px",color:"#6B7094",fontSize:"11px"}}>{rep.title}</td>
                      <td style={{padding:"8px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontWeight:700,fontSize:"11px",color:"#1A1C2E"}}>{rep.annual_quota?"$"+Math.round(rep.annual_quota).toLocaleString("en-US"):"—"}</td>
                      <td style={{padding:"8px 8px",textAlign:"center",fontSize:"10px",color:rep.ramp_months>0?"#C0392B":"#A0A3B8",fontWeight:rep.ramp_months>0?600:400}}>
                        {rep.ramp_months > 0 ? <span style={{cursor:"pointer",borderBottom:"1px dashed rgba(192,57,43,0.4)"}} onClick={e=>{
                          const rect = e.currentTarget.getBoundingClientRect();
                          setExpandedRamps(prev=>prev[rep.id]?{}:{[rep.id]:{top:rect.bottom+4,left:rect.left+rect.width/2}});
                        }}>
                          {rep.ramp_months} mo ramp {expandedRamps[rep.id]?"▾":"▸"}
                        </span> : "No ramp"}
                        {rep.ramp_months > 0 && expandedRamps[rep.id] && <div style={{position:"fixed",top:expandedRamps[rep.id].top,left:expandedRamps[rep.id].left,transform:"translateX(-50%)",zIndex:1000,background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.12)",borderRadius:"8px",padding:"8px 12px",boxShadow:"0 4px 12px rgba(0,0,0,0.1)",whiteSpace:"nowrap",textAlign:"left",fontSize:"10px"}}>
                          <div style={{fontWeight:700,color:"#1A1C2E",marginBottom:"4px",fontSize:"10px"}}>Ramp Schedule</div>
                          {rep.ramp_schedule.map((pct,i)=><div key={i} style={{color:"#C0392B",fontFamily:"'Space Mono',monospace",padding:"1px 0"}}>Mo {i+1}: {pct}%</div>)}
                          <div style={{color:"#81B29A",fontFamily:"'Space Mono',monospace",padding:"1px 0",borderTop:"1px solid rgba(0,0,0,0.06)",marginTop:"2px",paddingTop:"3px"}}>Mo {rep.ramp_schedule.length+1}+: 100%</div>
                          {rep.startDate && <div style={{color:"#8B8FA3",marginTop:"4px",fontSize:"9px"}}>Hired: {rep.startDate}</div>}
                        </div>}
                      </td>
                      {forecastMonths.map(m=><td key={m} style={{padding:"8px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontSize:"10px",color:"#1A1C2E",fontWeight:500}}>{fmtK(rep.months[m]||0)}</td>)}
                      <td style={{padding:"8px 8px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontSize:"11px",fontWeight:700,color:"#1A1C2E"}}>{fmtK(repTotal)}</td>
                    </tr>
                  </Fragment>;
                })}

                {/* Totals row */}
                <tr style={{borderTop:"2px solid rgba(0,0,0,0.08)",background:"rgba(0,0,0,0.015)"}}>
                  <td style={{...SL,padding:"10px 12px",fontWeight:700,fontSize:"12px",color:"#1A1C2E",background:"#FAFAFA"}}>Total</td>
                  <td></td>
                  <td style={{padding:"10px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontWeight:700,fontSize:"11px",color:"#81B29A"}}>{fmtK(totalQuota)}</td>
                  {/* was colSpan={2} — Distribution + Ramp; the Distribution column is retired. */}
                  <td></td>
                  {forecastMonths.map(m=><td key={m} style={{padding:"10px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontWeight:700,fontSize:"10px",color:"#1A1C2E"}}>{fmtK(totalsByMonth[m])}</td>)}
                  <td style={{padding:"10px 8px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontWeight:700,fontSize:"12px",color:"#C0392B"}}>{fmtK(totalACV)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer note */}
          <div style={{marginTop:"16px",padding:"14px 18px",borderRadius:"10px",background:"rgba(61,133,198,0.04)",border:"1px solid rgba(61,133,198,0.1)"}}>
            <div style={{fontSize:"11px",color:"#3D85C6",fontWeight:600}}>Monthly New ACV = Annual Quota × Monthly Factor × Ramp %</div>
            {/* ★ THE SCOPE CONTRAST IS NOW EXPLICIT (Brian, 2026-09-15).
                The retired tail read "Wiring to Revenue engine and commissions coming in a
                future update." ⛔ BOTH CLAUSES WERE STALE, and measurably so: ramp reaches
                BOTH the revenue engine AND commissions. `runFullModel`'s monthlyNewACV block
                multiplies by rampPct and feeds computeAEQuotaDR → the IS; its commissionData
                block computes `empComm[m] = r2(acv * commRate)` on the SAME ramped `acv` and
                feeds computePayroll. ⛔ The "half true" reading is wrong — nothing is missing.
                ⓘ The note was TRUE WHEN WRITTEN (`ecdc80e`, 2026-03-25, subject "ramp
                  display"); both wirings landed together 14 days later in `4d62cef`. It was
                  wrong for ~5½ months.
                ★ AND WHY THE NEW WORDING SAYS WHAT IT SAYS: "Monthly Factor" is now
                  COMPANY-WIDE while Ramp % is PER REP. The old text sat directly beside
                  "per AE" with nothing distinguishing the two scopes. The pointer to the
                  Payroll page is KEPT deliberately so a tenant user knows where to look.
                ⛔ It names no panel header: the header is tenant-variable (cid=36 renders the
                  routed line's name, "Enterprise Revenue", via moduleLineLabel). */}
            <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>Monthly Factor is the company-wide distribution above — a seasonal percentage, or 1/12 under Straight Line. Ramp % is per AE and is configured on the Payroll page.</div>
          </div>
        </div>;
      })()}

      {/* ═══ COMPANY QUOTA ═══ */}
      {effectiveView==="company_quota"&&(()=>{
        const cqDR = companyQuotaDR || { currentARR:{}, newACV:{}, grossRenewals:{}, churn:{}, netRenewals:{}, totalACV:{}, gaapRevenue:{}, deferredBalance:{}, cashReceived:{}, schedule:[] };
        /* W-036 (A) (Brian, 10-01): the panel SAYS when it is unsaved. Its inputs are live drafts while the table below is
           built only from SAVED figures (measured, diffs/group3-phase0.txt: an unsaved edit moves 0 cells), so a draft
           that differs from the saved row is named beside Save. ONE rule: keepDirtyDraft's own comparison (sameValue)
           against the stored snapshot the refetch effect keeps (cqPrevRef) -- no second notion of "dirty". `enabled` is
           left out: its toggle persists on click (G3), so it is never an unsaved change. The marker clears on Save (the
           refetch re-seeds the saved fields) and on a refetch whose stored values match the draft. */
        const cqSavedSnap = cqPrevRef.current;
        const cqDraftNow = { start_mode: cqStartMode, opening_arr: cqOpeningArr, growth_type: cqGrowthType, growth_mode: cqGrowthMode,
          growth_inputs: cqGrowthInputs, renewal_mode: cqRenewalMode, churn_rate: cqChurnRate, manual_renewals: cqManualRenewals,
          opening_bs_balance: cqOpeningBal, existing_total_acv: cqExistingACV, existing_monthly_rev: cqExistingRev, growth_period: cqGrowthPeriod };
        const cqUnsaved = !!cqSavedSnap && Object.keys(cqDraftNow).some(k => !sameValue(cqDraftNow[k], cqSavedSnap[k]));
        /* W-036 (A): the waterfall's month-1 Opening ARR reads the SAVED row, as the engine does (computeCompanyQuotaDR
           reads parseFloat(settings.opening_arr)) -- not page state -- so every cell of the table is one kind: saved.
           This page has no Opening ARR control (the Deferred Revenue tab owns opening_arr and start_mode and saves the
           resolved figure), so the value is unchanged on every tenant (measured equal); only its source is single now. */
        const cqSavedRow = rawData?.cqSettings || {};
        const cqSavedOpening = (cqSavedRow.start_mode || 'zero') === 'existing' ? (parseFloat(cqSavedRow.opening_arr) || 0) : 0;
        const cqIS={width:"100%",padding:"6px 8px",borderRadius:"6px",border:"1px solid rgba(0,0,0,0.12)",fontSize:"12px",fontFamily:"'Space Mono',monospace",background:"#F8F9FB"};
        const cqLblS={fontSize:10,fontWeight:700,color:"#6B7280",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8,paddingBottom:4,borderBottom:"1px solid #E2E5F0"};
        const cqHelpS={fontSize:9,color:"#8B8FA3",marginTop:4};
        /* C1 -- the "Full Year" box's value, DERIVED from the one map rather than mirrored
           in a second state. First forecast month wins; blank or absent reads 0. */
        const cqAnnualValue = parseFloat(cqGrowthInputs[forecastMonths[0]]) || 0;

        return <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",padding:"24px"}}>
          <div style={{fontSize:"16px",fontWeight:700,color:"#1A1C2E",marginBottom:"6px"}}>{cqLineLabel + " — ARR Growth Revenue"}</div>
          <div style={{fontSize:"12px",color:"#8B8FA3",marginBottom:20}}>Company-level ARR target with growth, renewals, churn, and GAAP recognition.</div>

          {/* Enable toggle */}
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16,padding:"10px 14px",borderRadius:8,background:cqEnabled?"rgba(129,178,154,0.06)":"rgba(0,0,0,0.03)",border:"1px solid "+(cqEnabled?"rgba(129,178,154,0.15)":"rgba(0,0,0,0.08)")}}>
            <span style={{fontSize:12,fontWeight:700,color:cqEnabled?"#1A1C2E":"#8B8FA3"}}>Company Quota Revenue</span>
            <ViewOnly canEdit={canEditModel}><button onClick={()=>toggleCqEnabled(!cqEnabled)} style={{padding:"4px 14px",borderRadius:6,border:"none",background:cqEnabled?"#81B29A":"#D1D5DB",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}}>{cqEnabled?"Enabled":"Disabled"}</button></ViewOnly>
            {!cqEnabled && <span style={{fontSize:10,color:"#8B8FA3",fontStyle:"italic"}}>Revenue will not flow to IS/BS/CFS while disabled</span>}
            {cqConflict?.at === 'toggle' && <span data-stamp-conflict="cq-toggle" style={{marginLeft:10,fontSize:11,fontWeight:600,color:"#E07A5F"}}>{cqConflict.msg}</span>}
          </div>
          {showCqDisableWarning && <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.4)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowCqDisableWarning(false)}>
            <div style={{background:"#fff",borderRadius:12,padding:24,maxWidth:420,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
              <div style={{fontSize:14,fontWeight:700,color:"#1A1C2E",marginBottom:8}}>Disable Company Quota Revenue?</div>
              <div style={{fontSize:12,color:"#6B7094",marginBottom:16}}>Disabling Company Quota will remove its revenue from IS, BS, and CFS. Your data is preserved and can be re-enabled. Continue?</div>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <button onClick={()=>setShowCqDisableWarning(false)} style={{padding:"7px 16px",borderRadius:6,border:"1px solid #D1D5DB",background:"#fff",color:"#4B5563",fontSize:11,fontWeight:600,cursor:"pointer"}}>Cancel</button>
                <ViewOnly canEdit={canEditModel}><button onClick={confirmCqDisable} style={{padding:"7px 16px",borderRadius:6,border:"none",background:"#E07A5F",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}}>Disable</button></ViewOnly>
              </div>
            </div>
          </div>}

          {/* Settings Panel */}
          <ViewOnly canEdit={canEditModel}><div style={{marginBottom:24,padding:16,background:"#FAFBFE",borderRadius:8,border:"1px solid #E2E5F0"}}>
            <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:16}}>
              <div style={{minWidth:140}}>
                <div style={cqLblS}>Growth Type</div>
                <select value={cqGrowthType} onChange={e=>{/* W-009 (C2): ONE switch behaviour for both panels -- utils/growthTypeSwitch.js */const sw=growthTypeSwitch({from:cqGrowthType,to:e.target.value,values:cqGrowthInputs});if(sw){setCqGrowthType(sw.type);setCqGrowthInputs(sw.values);}}} style={{...cqIS,textAlign:"left",cursor:"pointer"}}>
                  <option value="flat">Flat Dollar</option>
                  <option value="pct">Percentage</option>
                </select>
              </div>
              {cqGrowthType==='pct' && <div style={{minWidth:160}}>
                <div style={cqLblS}>Growth Mode</div>
                <select value={cqGrowthMode} onChange={e=>setCqGrowthMode(e.target.value)} style={{...cqIS,textAlign:"left",cursor:"pointer"}}>
                  <option value="compounding">Compounding</option>
                  <option value="flat">Flat (% of opening ARR)</option>
                  <option value="net_arr">Net ARR (prior + new − churn)</option>
                </select>
                {/* WHAT SEPARATES THE THREE MODES IS WHICH BASE THE PERCENTAGE IS APPLIED TO,
                    and nothing else. MEASURED on real settings: compounding grows off prior
                    GROSS ARR, net_arr off prior ARR with churn removed, flat off the fixed
                    opening ARR. The two compounding modes first diverge in month 2 by $151.29
                    and reach $1,929.10 by month 9 -- and with churn_rate = 0 they produce
                    IDENTICAL series in all 9 months, which is the control proving churn is the
                    ENTIRE difference.

                    WORDING CORRECTED 2026-09-17 (Brian's own text, shipped verbatim). The
                    previous caption read "Compounding = prior ARR including churned revenue",
                    which was not merely long -- it was WRONG. "Including churned revenue"
                    implies churn happened and is still being counted. It has not: compounding
                    grows off prior GROSS ARR, from which churn has NEVER been deducted. Saying
                    "gross ARR (churn not deducted)" states what the code does.

                    ONE caption for all three options: the reader is choosing BETWEEN them, so
                    a per-option note would have to be read three times to answer one question. */}
                <div style={cqHelpS}>The % grows on: Compounding = gross ARR (churn not deducted) · Net ARR = ARR after churn · Flat = the fixed opening ARR. With 0% churn the first two match.</div>
              </div>}
              <div style={{minWidth:160}}>
                <div style={cqLblS}>Growth Period</div>
                <select value={cqGrowthPeriod} onChange={e=>applyGrowthPeriod(e.target.value)} style={{...cqIS,textAlign:"left",cursor:"pointer"}}>
                  <option value="individual">Individual Months</option>
                  <option value="annual">All forecast months (same value)</option>
                </select>
              </div>
            </div>

            {/* Growth inputs */}
            <div style={{marginBottom:16}}>
              <div style={cqLblS}>{cqGrowthType==='flat'?"Monthly ARR Growth ($)":"Monthly ARR Growth (%)"}</div>
              {cqGrowthPeriod==='annual' ? (
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  {cqGrowthType==='flat' && <span style={{fontSize:11,color:"#6B7280"}}>$</span>}
                  {/* C1 -- THIS BOX READS AND WRITES cqGrowthInputs DIRECTLY. It used to read
                      and write cqAnnualGrowth, a separate state the load path never seeded,
                      so it rendered BLANK on every live tenant while a real rate was stored
                      -- and Save wrote that blank as 0 to every month. Now it shows the value
                      applyGrowthPeriod just filled, and a change writes every month at once. */}
                  <input type="text" defaultValue={cqAnnualValue?cqAnnualValue.toLocaleString("en-US"):""}
                    key={"cqag-"+cqAnnualValue}
                    onFocus={e=>{e.target.value=cqAnnualValue?String(cqAnnualValue):"";e.target.select();}}
                    onBlur={e=>{const v=parseFloat(e.target.value.replace(/,/g,""))||0;setCqGrowthInputs(p=>{const n={};forecastMonths.forEach(m=>{n[m]=v;});return n;});e.target.value=v?v.toLocaleString("en-US"):"";}}
                    style={{...cqIS,width:130,textAlign:"right"}} />
                  {cqGrowthType==='pct' && <span style={{fontSize:11,color:"#6B7280"}}>%</span>}
                  {/* ★★ S4 — THE SITE NO FILING NAMED, found by the extractor's own anchor abort.
                      H2 was filed as ONE caption (the misc-line one, below); the SENTENCE FAMILY
                      has TWO members, eight characters apart ("all 12 months" vs "all 12 forecast
                      months"), and a grep keyed on the exact filed string sees one of them.
                      ⇒ Same class as S3 and equally inert: the writer at :397-398 is
                        `forecastMonths.reduce((o,m)=>{o[m]=cqAnnualGrowth;return o;},{})` — the
                        tenant's whole window, correct at any length. Only the caption said twelve.
                      ⛔ FIXED HERE RATHER THAN FILED: leaving it would put a corrected caption and
                        an uncorrected twin on the SAME PAGE, which is §6's "a second implementation
                        is a place for a fix to fail to arrive" — and visible in one sitting. */}
                  {/* NOTE 2026-09-17 -- the S4 comment ABOVE cites a writer that NO LONGER
                      EXISTS: the cqGrowthPeriod branch and cqAnnualGrowth were both retired
                      when the "Full Year" wipe was closed. Its CLAIM about this caption is
                      unchanged and is now stronger -- the value really is applied to all
                      forecastMonths.length months, and it is applied the moment "Full Year"
                      is selected rather than at Save.
                      Filed as a separate block so no pre-existing line is rewritten: the one
                      above carries an em dash, and editing it would have put a non-ASCII byte
                      into a line this commit ADDS (rule 55). */}
                  <span style={{fontSize:9,color:"#8B8FA3"}}>Same value applied to all {forecastMonths.length} forecast month{forecastMonths.length === 1 ? "" : "s"}</span>
                </div>
              ) : (
                <div style={{display:"flex",gap:4}}>
                  {forecastMonths.map(m=>(
                    <div key={m} style={{flex:1,minWidth:0,maxWidth:80}}>
                      <div style={{fontSize:8,color:"#8B8FA3",textAlign:"center",marginBottom:2}}>{formatMonthLabel(m, "shortYear")}</div>
                      <input type="text" defaultValue={(cqGrowthInputs[m]||0)?(cqGrowthInputs[m]||0).toLocaleString("en-US"):""}
                        key={"cqgi-"+m+"-"+(cqGrowthInputs[m]||0)}
                        onFocus={e=>{const v=cqGrowthInputs[m]||0;e.target.value=v?String(v):"";e.target.select();}}
                        onBlur={e=>{const v=parseFloat(e.target.value.replace(/,/g,""))||0;setCqGrowthInputs(p=>({...p,[m]:v}));e.target.value=v?v.toLocaleString("en-US"):"";}}
                        placeholder="0" style={{...cqIS,width:"100%",padding:"3px 4px",fontSize:9,textAlign:"right"}} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Renewal / Churn */}
            <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:16}}>
              <RenewalModeSelect mode={cqRenewalMode} onModeChange={setCqRenewalMode} />
              <ChurnRateInput churnRate={cqChurnRate} onChurnRateChange={setCqChurnRate} />
            </div>

            {/* Manual renewals grid */}
            <ManualRenewalsGrid mode={cqRenewalMode} renewals={cqManualRenewals} onRenewalsChange={setCqManualRenewals} months={forecastMonths} />

            <button onClick={saveCqSettings} disabled={cqSaving} style={{padding:"7px 20px",borderRadius:6,border:"none",background:cqSaving?"#A0A3B8":"linear-gradient(135deg,#3D85C6,#5A5FC0)",color:"#fff",fontSize:11,fontWeight:700,cursor:cqSaving?"not-allowed":"pointer"}}>{cqSaving?"Saving...":"Save Settings"}</button>
            {cqUnsaved && <span data-cq-unsaved style={{marginLeft:10,fontSize:11,color:"#8B8FA3",fontStyle:"italic"}}>{"Unsaved changes \u2014 Save to update the table below."}</span>}
            {cqConflict?.at === 'save' && <span data-stamp-conflict="cq-save" style={{marginLeft:10,fontSize:11,fontWeight:600,color:"#E07A5F"}}>{cqConflict.msg}</span>}
            {/* C3(b) -- THE SAME CLASS OF NOTE AS THE IMPLEMENTATION PANEL'S, WITH DELIBERATELY
                DIFFERENT WORDING, AND THE DIFFERENCE IS MEASURED:
                - Implementation's table reads PURELY from saved state, so its note can speak
                  about "the figures" wholesale. THIS panel is a HYBRID: the inputs above are
                  LIVE form state while the waterfall below reads cqDR, the engine's output
                  from the LAST SAVED settings. A sentence claiming the panel reflects saved
                  values would be FALSE here.
                - It therefore names the two rows it is true of. MEASURED: "ARR Growth" reads
                  cqDR.newACV and "Churn" reads cqDR.churn, both saved-derived. "Opening ARR"
                  reads cqOpeningArr, which this page carries but has NO control for (it is
                  owned by the Deferred Revenue tab), so it is not part of the claim.
                - It says "below" because the waterfall sits BELOW this button. Implementation's
                  says "above" because its table sits above its own button -- copying the word
                  rather than checking the layout would have shipped a caption pointing the
                  wrong way.
                - Reuses cqHelpS, the panel's existing 9px caption style. No new component, no
                  icon, and NOT amber -- amber already means "routing unanswered" on this page. */}
            {canEditModel?<div style={{...cqHelpS,marginTop:8}}>The ARR Growth and Churn figures below reflect the last saved settings, so changes appear after you Save.</div>:<div style={{...cqHelpS,marginTop:8}}><ViewOnlyNote/></div>}
          </div></ViewOnly>

          {/* Display Table */}
          {cqEnabled && <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px",minWidth:`${forecastMonths.length*80+220}px`}}>
              <thead>
                <tr style={{borderBottom:"2px solid rgba(0,0,0,0.08)"}}>
                  <th style={{...SL,padding:"10px 12px",textAlign:"left",fontSize:"10px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase",minWidth:"200px",background:"#FFFFFF"}}>{cqLineLabel}</th>
                  {forecastMonths.map(m=><th key={m} style={{padding:"10px 4px",textAlign:"right",fontSize:"9px",fontWeight:600,color:"#1A1C2E",whiteSpace:"nowrap"}}>{formatMonthLabel(m, "shortYear")}</th>)}
                  <th style={{padding:"10px 8px",textAlign:"right",fontSize:"10px",fontWeight:700,color:"#1A1C2E",textTransform:"uppercase"}}>Total</th>
                </tr>
              </thead>
              <tbody>
                {(()=>{
                  // Build chained ARR balances: Total ARR[m] = Opening[m] + Growth[m] + Churn[m]
                  const cqDisplayARR = {};
                  forecastMonths.forEach((m,i) => {
                    const open = i === 0 ? cqSavedOpening : (cqDisplayARR[forecastMonths[i-1]] || 0);
                    cqDisplayARR[m] = r2(open + (cqDR.newACV[m]||0) + (cqDR.churn[m]||0));
                  });
                  return [
                    ["Opening ARR",m=>{const i=forecastMonths.indexOf(m);return i===0?cqSavedOpening:(cqDisplayARR[forecastMonths[i-1]]||0);},false,false],
                    ["ARR Growth",m=>cqDR.newACV[m]||0,false,true],
                    ["Churn",m=>cqDR.churn[m]||0,true,true],
                    ["Net ARR",m=>cqDisplayARR[m]||0,false,false],
                  ];
                })().map(([label,fn,isRed,showTotal])=>{
                  const total = r2(forecastMonths.reduce((s,m)=>s+fn(m),0));
                  const isTotal = label==="Net ARR";
                  return <tr key={label} style={{borderBottom:isTotal?"2px solid rgba(0,0,0,0.08)":"1px solid rgba(0,0,0,0.04)",background:isTotal?"rgba(0,0,0,0.015)":"transparent"}}>
                    <td style={{...SL,padding:"8px 12px",fontWeight:isTotal?700:500,fontSize:"11px",color:isRed?"#C0392B":"#1A1C2E",background:isTotal?"#FAFAFA":"#FFFFFF"}}>{label}</td>
                    {forecastMonths.map(m=>{const v=fn(m);return <td key={m} style={{padding:"8px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontSize:"10px",fontWeight:isTotal?700:500,color:isRed&&v<0?"#C0392B":"#1A1C2E"}}>{v===0?"\u2014":fmt(v)}</td>;})}
                    <td style={{padding:"8px 8px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontSize:"11px",fontWeight:700,color:isRed&&total<0?"#C0392B":"#1A1C2E"}}>{showTotal?fmt(total):"\u2014"}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>}
        </div>;
      })()}

      {/* ═══ MISC REVENUE ═══ */}
      {effectiveView==="misc_rev"&&(()=>{
        const lines = ctxMiscLines || [];
        const mIS={width:"100%",padding:"6px 8px",borderRadius:"6px",border:"1px solid rgba(0,0,0,0.12)",fontSize:"12px",fontFamily:"'Space Mono',monospace",background:"#F8F9FB"};
        const mLblS={fontSize:10,fontWeight:700,color:"#6B7280",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4};

        return <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",padding:"24px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"20px"}}>
            <div>
              <div style={{fontSize:"16px",fontWeight:700,color:"#1A1C2E",marginBottom:"6px"}}>Misc Revenue Lines</div>
              <div style={{fontSize:"12px",color:"#8B8FA3"}}>User-defined revenue lines that flow directly to the Income Statement. No deferred revenue.</div>
            </div>
            <ViewOnly canEdit={canEditModel}><button onClick={async()=>{if(lines.length>=10)return;const created=await createMiscRevenueLine(companyId);const newId=created?.data?.id;if(newId!=null)setMiscRouteUnanswered(prev=>({...prev,[newId]:true}));markStale();await ensureFresh();}} disabled={lines.length>=10} style={{padding:"8px 18px",borderRadius:"8px",border:"none",background:lines.length>=10?"#D1D5DB":"linear-gradient(135deg,#3D85C6,#5A5FC0)",color:"#fff",cursor:lines.length>=10?"not-allowed":"pointer",fontSize:"11px",fontWeight:700,fontFamily:"'DM Sans',sans-serif",opacity:lines.length>=10?0.6:1}}>{lines.length>=10?"Maximum 10 lines reached":"+ Add Revenue Line"}</button></ViewOnly>
          </div>

          {lines.length===0 && <div style={{padding:"40px",textAlign:"center",color:"#8B8FA3",fontSize:13}}>No misc revenue lines yet.{canEditModel?' Click "+ Add Revenue Line" to create one.':<> <ViewOnlyNote/></>}</div>}

          {lines.map((line,idx) => {
            const lineEnabled = line.enabled !== false;
            const edit = getMiscEdit(line.id);
            const localMa = edit.monthly_amounts || {};
            const saving = miscSaving[line.id];
            const saved = miscSaved[line.id];
            /* W-009 (C1): the route choice offers every UNCLAIMED revenue line the income statement
               shows -- so a misc line can resume a historical revenue line the tenant chose not to
               forecast at onboarding (Brian, 09-23) -- minus every line another revenue module claims
               (AE Quota / Company Quota / an implementation line / another misc line). ONE derivation,
               utils/miscTargets.js; the server applies the same rule (validateMiscTarget) and the build
               verify asserts they agree on every tenant. Labels come from the line's OWNER
               (gl_line_mappings.display_name), never the raw key or the rmm cache.
               It REPLACES the old source -- the rmm rows with module_type='misc_revenue' -- which made
               that case unreachable on screen. This line's own current target stays selectable
               (it is never 'claimed by another'), and is force-included defensively below. */
            const availableMappings = miscTargetOptions({ isLines: rawData?.isLines, glMappings: rawData?.glMappings, revMappings: rawData?.revMappings, implementationLines: rawData?.implementationLines, miscLines: lines, lineId: line.id });
            const routeLabel = (key) => miscTargetLabel(rawData?.glMappings, key);
            const routeErr = miscRouteError[line.id];
            const delLine = async () => {
              const ma = line.monthly_amounts || {};
              const hasData = Object.values(ma).some(v => (parseFloat(v) || 0) !== 0);
              if (hasData && !window.confirm("Deleting this line will remove its revenue from the IS. This cannot be undone. Continue?")) return;
              await deleteMiscRevenueLine(companyId, line.id);
              markStale();
              await ensureFresh();
            };
            const toggleEnabled = async () => {
              if (lineEnabled) {
                const ma = line.monthly_amounts || {};
                const hasData = Object.values(ma).some(v => (parseFloat(v) || 0) !== 0);
                if (hasData) { setMiscDisableWarning(line.id); return; }
              }
              await updateMiscRevenueLine(companyId, line.id, { enabled: !lineEnabled });
              markStale();
              await ensureFresh();
            };
            const confirmDisable = async () => {
              setMiscDisableWarning(null);
              await updateMiscRevenueLine(companyId, line.id, { enabled: false });
              markStale();
              await ensureFresh();
            };
            const moveUp = async () => {
              if (idx === 0) return;
              const items = lines.map((l,i) => ({ id: l.id, sort_order: i }));
              items[idx].sort_order = idx - 1;
              items[idx - 1].sort_order = idx;
              await reorderMiscRevenueLines(companyId, items);
              markStale();
              await ensureFresh();
            };
            const moveDown = async () => {
              if (idx === lines.length - 1) return;
              const items = lines.map((l,i) => ({ id: l.id, sort_order: i }));
              items[idx].sort_order = idx + 1;
              items[idx + 1].sort_order = idx;
              await reorderMiscRevenueLines(companyId, items);
              markStale();
              await ensureFresh();
            };

            return <div key={line.id} style={{marginBottom:16,padding:16,background:"#FAFBFE",borderRadius:8,border:"1px solid #E2E5F0",opacity:lineEnabled?1:0.6}}>
              <ViewOnly canEdit={canEditModel}><div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                {/* Reorder buttons */}
                <div style={{display:"flex",flexDirection:"column",gap:2}}>
                  <button onClick={moveUp} disabled={idx===0} style={{padding:"2px 6px",border:"1px solid #D1D5DB",borderRadius:4,background:"#fff",cursor:idx===0?"default":"pointer",fontSize:10,color:idx===0?"#D1D5DB":"#4B5563",lineHeight:1}}>▲</button>
                  <button onClick={moveDown} disabled={idx===lines.length-1} style={{padding:"2px 6px",border:"1px solid #D1D5DB",borderRadius:4,background:"#fff",cursor:idx===lines.length-1?"default":"pointer",fontSize:10,color:idx===lines.length-1?"#D1D5DB":"#4B5563",lineHeight:1}}>▼</button>
                </div>
                {/* Name */}
                <input type="text" maxLength={100} value={edit.name||""} onChange={e=>setMiscField(line.id,"name",e.target.value)} placeholder="Revenue Line Name" style={{...mIS,flex:1,maxWidth:300,textAlign:"left",fontFamily:"'DM Sans',sans-serif",fontWeight:600}} />
                {/* Enable toggle */}
                <button onClick={toggleEnabled} style={{padding:"4px 12px",borderRadius:6,border:"none",background:lineEnabled?"#81B29A":"#D1D5DB",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}}>{lineEnabled?"Enabled":"Disabled"}</button>
                {/* Include in ARR */}
                <label style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer",fontSize:10,fontWeight:600,color:edit.include_in_arr?"#3D85C6":"#8B8FA3"}}>
                  <input type="checkbox" checked={!!edit.include_in_arr} onChange={e=>setMiscField(line.id,"include_in_arr",e.target.checked)} style={{cursor:"pointer"}} />
                  Include in ARR
                </label>
                {/* Delete */}
                <button onClick={delLine} style={{padding:"4px 10px",borderRadius:6,border:"1px solid rgba(224,122,95,0.3)",background:"transparent",color:"#E07A5F",fontSize:10,fontWeight:600,cursor:"pointer"}}>Delete</button>
              </div></ViewOnly>
              {miscDisableWarning===line.id && <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.4)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setMiscDisableWarning(null)}>
                <div style={{background:"#fff",borderRadius:12,padding:24,maxWidth:420,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
                  <div style={{fontSize:14,fontWeight:700,color:"#1A1C2E",marginBottom:8}}>Disable '{line.name}'?</div>
                  <div style={{fontSize:12,color:"#6B7094",marginBottom:16}}>Disabling '{line.name}' will remove its revenue from the IS. Your data is preserved and can be re-enabled. Continue?</div>
                  <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                    <button onClick={()=>setMiscDisableWarning(null)} style={{padding:"7px 16px",borderRadius:6,border:"1px solid #D1D5DB",background:"#fff",color:"#4B5563",fontSize:11,fontWeight:600,cursor:"pointer"}}>Cancel</button>
                    <ViewOnly canEdit={canEditModel}><button onClick={confirmDisable} style={{padding:"7px 16px",borderRadius:6,border:"none",background:"#E07A5F",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}}>Disable</button></ViewOnly>
                  </div>
                </div>
              </div>}

              {lineEnabled && <ViewOnly canEdit={canEditModel}>
                {/* 5e-3: route choice — where this line's forecast lands on the IS */}
                <div style={{marginBottom:12,padding:"10px 12px",background:edit.target_gl_line_key?"#EEF4FB":"#fff",borderRadius:6,border:"1px solid #E2E5F0"}}>
                  <div style={mLblS}>Income Statement Line</div>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <select
                      value={miscRouteUnanswered[line.id] ? "" : (edit.target_gl_line_key ? "route" : "new")}
                      onChange={e=>{
                        if (miscRouteUnanswered[line.id]) { setMiscRouteUnanswered(prev=>{ const n={...prev}; delete n[line.id]; return n; }); setMiscSaveError(prev=>({...prev,[line.id]:null})); }
                        if (e.target.value === "new") { setMiscField(line.id,"target_gl_line_key",""); }
                        else { setMiscField(line.id,"target_gl_line_key", edit.target_gl_line_key || (availableMappings[0]?.key || "")); }
                      }}
                      style={{...mIS,textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",maxWidth:200,...(miscRouteUnanswered[line.id]?{border:"1px solid #E0A458"}:{})}}>
                      {miscRouteUnanswered[line.id] && <option value="" disabled>{"\u2014 choose where this line appears \u2014"}</option>}
                      <option value="new">Create new IS line</option>
                      <option value="route" disabled={availableMappings.length===0 && !edit.target_gl_line_key}>Route to existing IS line</option>
                    </select>
                    {edit.target_gl_line_key && (
                      <select value={edit.target_gl_line_key} onChange={e=>setMiscField(line.id,"target_gl_line_key",e.target.value)}
                        style={{...mIS,textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",maxWidth:260}}>
                        {/* defensive: keep the line's own current target selectable even if not in `available` */}
                        {!availableMappings.some(m=>m.key===edit.target_gl_line_key) && <option value={edit.target_gl_line_key}>{routeLabel(edit.target_gl_line_key)}</option>}
                        {availableMappings.map(m=><option key={m.key} value={m.key}>{m.label}</option>)}
                      </select>
                    )}
                    {miscRouteUnanswered[line.id]
                      ? <span style={{fontSize:9,color:"#B07219",fontWeight:600}}>{"Required \u2014 choose an existing line or Create new IS line"}</span>
                      : edit.target_gl_line_key
                      ? <span style={{fontSize:9,color:"#3D85C6",fontWeight:600}}>→ {routeLabel(edit.target_gl_line_key)} (existing IS line — no separate row)</span>
                      : <span style={{fontSize:9,color:"#8B8FA3"}}>emits its own line ({miscSyntheticKey(line.id)})</span>}
                  </div>
                  {routeErr && <div style={{fontSize:10,color:"#E07A5F",marginTop:6,fontWeight:600}}>{routeErr}</div>}
                  {/* Batch 1 (2026-11-03, L3649): a % of Revenue vendor whose base is this line's IS line moves with it. */}
                  <div data-misc-pct-note="" style={{fontSize:9,color:"#8B8FA3",marginTop:6}}>{"A vendor costed as a % of Revenue of this line follows it: change where this line appears and that vendor\u2019s cost moves with it."}</div>
                </div>
                <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:12}}>
                  <div style={{minWidth:130}}>
                    <div style={mLblS}>Growth Type</div>
                    <select value={edit.growth_type||'flat'} onChange={e=>{/* W-009 (C2): ONE switch behaviour for both panels -- utils/growthTypeSwitch.js */const sw=growthTypeSwitch({from:edit.growth_type||'flat',to:e.target.value,values:localMa});if(sw)setMiscEdits(prev=>({...prev,[line.id]:{...prev[line.id],growth_type:sw.type,monthly_amounts:sw.values}}));}} style={{...mIS,textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                      <option value="flat">Flat Dollar</option>
                      <option value="pct">Percentage</option>
                    </select>
                  </div>
                  {edit.growth_type==='pct' && <div style={{minWidth:140}}>
                    <div style={mLblS}>Growth Mode</div>
                    <select value={edit.growth_mode||'compounding'} onChange={e=>setMiscField(line.id,"growth_mode",e.target.value)} style={{...mIS,textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                      <option value="compounding">Compounding</option>
                      <option value="flat">Flat (% of first month)</option>
                    </select>
                  </div>}
                  <div style={{minWidth:160}}>
                    <div style={mLblS}>Growth Period</div>
                    <select value={edit.growth_period||'annual'} onChange={e=>setMiscField(line.id,"growth_period",e.target.value)} style={{...mIS,textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                      <option value="annual">All forecast months (same value)</option>
                      <option value="individual">Individual Months</option>
                    </select>
                  </div>
                </div>

                {/* Monthly amounts */}
                <div style={{marginBottom:12}}>
                  <div style={mLblS}>{edit.growth_type==='flat'?"Monthly Revenue ($)":"Monthly Values"}</div>
                  {(edit.growth_period||'annual')==='annual' ? (
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      {edit.growth_type==='flat' && <span style={{fontSize:11,color:"#6B7280"}}>$</span>}
                      <input type="text" defaultValue={(localMa[forecastMonths[0]]||0)?(localMa[forecastMonths[0]]||0).toLocaleString("en-US"):""} key={"ma-"+line.id+"-"+(localMa[forecastMonths[0]]||0)} onFocus={e=>{e.target.value=localMa[forecastMonths[0]]||"";e.target.select();}} onBlur={e=>{const v=parseFloat(e.target.value.replace(/,/g,""))||0;const newMa={};forecastMonths.forEach(m=>{newMa[m]=v;});setMiscField(line.id,"monthly_amounts",newMa);e.target.value=v?v.toLocaleString("en-US"):"";}} style={{...mIS,width:130,textAlign:"right"}} />
                      {edit.growth_type==='pct' && <span style={{fontSize:11,color:"#6B7280"}}>%</span>}
                      {/* ★ S3 — A LABEL, AND THE WRITER WAS ALREADY RIGHT. The onBlur one line
                          above writes `forecastMonths.forEach(m => newMa[m] = v)` — the tenant's
                          whole window, correct at any length. Only this caption said twelve, so
                          the behaviour never needed touching and MUST NOT be touched here.
                          ⛔ NOT the year-range idiom used on the payroll cards: this sentence is
                            about a VALUE APPLIED ACROSS MONTHS, not a total over a period, and
                            "applied to all 2026–2027" is not English. The honest window-driven
                            form of THIS sentence is the count. Two shapes, deliberately. */}
                      <span style={{fontSize:9,color:"#8B8FA3"}}>Same value applied to all {forecastMonths.length} month{forecastMonths.length === 1 ? "" : "s"}</span>
                    </div>
                  ) : (
                    <div data-misc-month-boxes="">
                    {/* Batch 1b (2026-11-03, L3593): a row pasted from Excel fills the boxes from the focused month on, each value read by
                        this grid's own rule (a blank cell = 0, as typing blank); a single value is typed as before (handleRowPaste). */}
                    <PasteHint />
                    <div style={{display:"flex",gap:4}}>
                      {forecastMonths.map(m=>(
                        <div key={m} style={{flex:1,minWidth:0,maxWidth:80}}>
                          <div style={{fontSize:8,color:"#8B8FA3",textAlign:"center",marginBottom:2}}>{formatMonthLabel(m, "shortYear")}</div>
                          <input type="text" defaultValue={(localMa[m]||0)?(localMa[m]||0).toLocaleString("en-US"):""} key={"mi-"+line.id+"-"+m+"-"+(localMa[m]||0)} onFocus={e=>{const v=localMa[m]||0;e.target.value=v?String(v):"";e.target.select();}} onBlur={e=>{const v=parseFloat(e.target.value.replace(/,/g,""))||0;setMiscField(line.id,"monthly_amounts",{...localMa,[m]:v});e.target.value=v?v.toLocaleString("en-US"):"";}}
                            onPaste={e=>handleRowPaste(e,{months:forecastMonths,month:m,onError:msg=>setMiscPasteErr(p=>({...p,[line.id]:msg})),
                              apply:cells=>{const n={...localMa};for(const [mo,v] of cells) n[mo]=v===null?0:v;setMiscField(line.id,"monthly_amounts",n);}})}
                            placeholder="0" style={{...mIS,width:"100%",padding:"3px 4px",fontSize:9,textAlign:"right"}} />
                        </div>
                      ))}
                    </div>
                    {miscPasteErr[line.id] && <div data-paste-error="" style={PASTE_ERROR_STYLE}>{miscPasteErr[line.id]}</div>}
                    </div>
                  )}
                </div>

                {/* Save button */}
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <button onClick={()=>saveMiscLine(line.id)} disabled={saving} style={{padding:"7px 20px",borderRadius:6,border:"none",background:saving?"#A0A3B8":"linear-gradient(135deg,#3D85C6,#5A5FC0)",color:"#fff",fontSize:11,fontWeight:700,cursor:saving?"not-allowed":"pointer"}}>{saving?"Saving...":"Save"}</button>
                  {saved && <span style={{fontSize:11,fontWeight:600,color:"#81B29A"}}>Saved</span>}
                  {miscSaveError[line.id] && <span style={{fontSize:11,fontWeight:600,color:"#E07A5F"}}>{miscSaveError[line.id]}</span>}
                </div>
              </ViewOnly>}
            </div>;
          })}
        </div>;
      })()}
      {effectiveView==="impl"&&(()=>{
        const lines = ctxImplLines || [];
        const implRev = ctxImplRevenue || {};
        const iIS={width:"100%",padding:"6px 8px",borderRadius:"6px",border:"1px solid rgba(0,0,0,0.12)",fontSize:"12px",fontFamily:"'Space Mono',monospace",background:"#F8F9FB"};
        const iLblS={fontSize:10,fontWeight:700,color:"#6B7280",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4};
        const iHelpS={fontSize:9,color:"#8B8FA3",marginTop:4};

        // Build source options
        const sourceOptions = [
          { value: 'ae_quota', label: aeLineLabel, key: 'ae_quota' },
          { value: 'company_quota', label: cqLineLabel, key: 'company_quota' },
          ...((ctxMiscLines || []).filter(l => l.enabled).map(l => ({ value: 'misc_rev', sourceId: l.id, label: l.name, key: `misc_rev_${l.id}` }))),
          { value: 'standalone', label: 'Standalone (Manual)', key: 'standalone' },
        ];

        return <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",padding:"24px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"20px"}}>
            <div>
              <div style={{fontSize:"16px",fontWeight:700,color:"#1A1C2E",marginBottom:"6px"}}>Implementation Revenue</div>
              <div style={{fontSize:"12px",color:"#8B8FA3"}}>Implementation fees tied to new client acquisitions from other revenue lines.</div>
            </div>
            <ViewOnly canEdit={canEditModel}><button onClick={async()=>{await createImplementationLine(companyId,{});markStale();await ensureFresh();}} style={{padding:"8px 18px",borderRadius:"8px",border:"none",background:"linear-gradient(135deg,#3D85C6,#5A5FC0)",color:"#fff",cursor:"pointer",fontSize:"11px",fontWeight:700,fontFamily:"'DM Sans',sans-serif"}}>+ Add Implementation Line</button></ViewOnly>
          </div>

          {lines.length===0 && <div style={{padding:"40px",textAlign:"center",color:"#8B8FA3",fontSize:13}}>No implementation lines yet.{canEditModel?' Click "+ Add Implementation Line" to create one.':<> <ViewOnlyNote/></>}</div>}

          {lines.map((line) => {
            const edit = getImplEdit(line.id);
            const lineEnabled = edit.enabled !== false;
            const saving = implSaving[line.id];
            const saved = implSaved[line.id];
            const lineRev = implRev[line.id] || {};
            /* 2026-10-27: the waterfall's rows follow the basis the engine APPLIED; a line the engine EXCLUDED returns nothing, so the
               STORED basis (the saved state, as every figure in the table) decides -- a % line with no percentage keeps its % rows. */
            const wfBasis = lineRev.impl_basis !== undefined ? lineRev.impl_basis : line.impl_basis;
            const wfPct = lineRev.impl_basis !== undefined ? lineRev.pct_of_new_acv : line.pct_of_new_acv;
            const localSa = edit.standalone_amounts || {};

            /* ═══ ★★ PART D — THE ROUTING PICKER'S MACHINERY ═══
             * THE SENTINEL IS THE SYNTHETIC KEY ITSELF, which is what BOTH precedents do:
             *   companies.sweep_interest_is_line_key stores the literal 'oi_interest';
             *   the Step-7 DR picker stores `deferred_rev_<module_type>` behind the label
             *   "Synthetic (forecast-only)".
             * ⇒ null and keep-separate therefore take the SAME ENGINE PATH deliberately, so the
             *   untested-branch count does not grow — and Part B preserves that: an unrouted
             *   line and a sentinel-as-synthetic line both land on rev_impl_<id>.
             * ★ THE WRINKLE NEITHER PRECEDENT HAS: this sentinel is PER-ROW, so it can only be
             *   written once the id exists. It is not needed at creation (the server refuses
             *   target_gl_line_key on the POST for exactly that reason) — a new line is born
             *   UNANSWERED, and by the time the operator answers, `line.id` is in hand. */
            const implSentinel = implSyntheticKey(line.id);

            /* ⛔ THE OPTION SOURCE CANNOT BE COPIED FROM MISC. Misc filters revMappings to
             *   module_type='misc_revenue'; MEASURED, 0 rmm rows carry module_type
             *   ='implementation' on any tenant, so that filter yields an EMPTY list and the
             *   control would be unusable. The honest source is the tenant's OWN emitted
             *   revenue leaves — the same set total_revenue sums.
             * ⛔ The line's own synthetic is excluded: routing a line to itself is the sentinel,
             *   which the control offers as its own explicit option rather than as a "target". */
            const implRevLeaves = (() => {
              const isl = rawData?.isLines || [];
              const total = isl.find(l => l.id === 'total_revenue');
              const ids = Array.isArray(total?.sumOf) ? total.sumOf : [];
              /* Batch 2 (2026-11-03, item 5): nor a misc line's OWN row (rev_misc_<id>, an un-routed misc line's stream) -- the server
               * refuses it (not a revenue line) and routing onto it would replace that misc revenue. Picker == server on every company. */
              const miscOwn = new Set((ctxMiscLines || []).map(m => miscSyntheticKey(m.id)));
              return ids.map(k => isl.find(l => l.id === k))
                        .filter(l => l && l.id !== implSentinel && !String(l.id).startsWith('rev_impl_') && !miscOwn.has(l.id));
            })();
            /* ★ CROSS-LINE UNIQUENESS, mirroring misc's: a leaf already claimed by ANOTHER
             *   implementation line is not offered. The server enforces it too (409) — the
             *   client filter is the courtesy, the server guard is the guarantee. */
            const implTargetedByOthers = new Set(
              lines.filter(l => l.id !== line.id && isRoutedElsewhere(l.id, l.target_gl_line_key))
                   .map(l => l.target_gl_line_key));
            /* W-016: and not a line another revenue stream claims -- AE / CQ targets and misc line targets (the server's
               revenueTargetClaims, mirrored by the ONE client derivation). The line's own current target stays offered. */
            const implClaimed = claimedRevenueTargets({ revMappings: rawData?.revMappings, miscLines: ctxMiscLines, implementationLines: lines, implLineId: line.id });
            const implAvailable = implRevLeaves.filter(l => (!implTargetedByOthers.has(l.id) && !implClaimed.has(l.id)) || l.id === edit.target_gl_line_key);
            const implRouteLabel = (key) => (implRevLeaves.find(l => l.id === key)?.label) || key;
            /* THE THREE STATES, read from ONE stored column — ⛔ never inferred from two flags. */
            const implRouteState = !edit.target_gl_line_key ? 'unanswered'
              : (edit.target_gl_line_key === implSentinel ? 'separate' : 'routed');
            const implRouteErr = implRouteError[line.id];
            /* ★ PART D — THE REFUSAL'S READER. Part B's warning finally reaches a screen. */
            const implLenWarn = (rawData?.implLengthWarnings || []).find(w => String(w.lineId) === String(line.id));

            const delLine = async () => {
              const hasData = lineRev.netImpl && Object.values(lineRev.netImpl).some(v => (v || 0) !== 0);
              if (hasData && !window.confirm(`Deleting '${edit.name}' will remove its revenue from IS/BS/CFS. Cannot be undone. Continue?`)) return;
              await deleteImplementationLine(companyId, line.id);
              markStale();
              await ensureFresh();
            };

            const toggleEnabled = async () => {
              if (lineEnabled) {
                const hasData = lineRev.netImpl && Object.values(lineRev.netImpl).some(v => (v || 0) !== 0);
                if (hasData) { setImplDisableWarning(line.id); return; }
              }
              setImplField(line.id, 'enabled', !lineEnabled);
              await updateImplementationLine(companyId, line.id, { enabled: !lineEnabled });
              markStale();
              await ensureFresh();
            };

            const confirmDisable = async () => {
              setImplDisableWarning(null);
              setImplField(line.id, 'enabled', false);
              await updateImplementationLine(companyId, line.id, { enabled: false });
              markStale();
              await ensureFresh();
            };

            const handleSourceChange = (val, sourceId) => {
              setImplField(line.id, 'source_type', val);
              setImplField(line.id, 'source_id', sourceId || null);
              const newName = 'Implementation - ' + getImplSourceName(val, sourceId);
              setImplField(line.id, 'name', newName);
            };

            const currentSourceKey = edit.source_type === 'misc_rev' ? `misc_rev_${edit.source_id}` : edit.source_type;

            return <div key={line.id} style={{marginBottom:16,padding:16,background:"#FAFBFE",borderRadius:8,border:"1px solid #E2E5F0",opacity:lineEnabled?1:0.6}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <span style={{fontSize:13,fontWeight:700,color:"#1A1C2E",fontFamily:"'DM Sans',sans-serif",flex:1}}>{edit.name || 'Implementation'}</span>
                <ViewOnly canEdit={canEditModel}>
                <button onClick={toggleEnabled} style={{padding:"4px 12px",borderRadius:6,border:"none",background:lineEnabled?"#81B29A":"#D1D5DB",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer"}}>{lineEnabled?"Enabled":"Disabled"}</button>
                <button onClick={delLine} style={{padding:"4px 10px",borderRadius:6,border:"1px solid rgba(224,122,95,0.3)",background:"transparent",color:"#E07A5F",fontSize:10,fontWeight:600,cursor:"pointer"}}>Delete</button>
                </ViewOnly>
              </div>

              {implDisableWarning===line.id && <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.4)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setImplDisableWarning(null)}>
                <div style={{background:"#fff",borderRadius:12,padding:24,maxWidth:420,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
                  <div style={{fontSize:14,fontWeight:700,color:"#1A1C2E",marginBottom:8}}>Disable '{edit.name}'?</div>
                  <div style={{fontSize:12,color:"#6B7094",marginBottom:16}}>Disabling '{edit.name}' will remove its revenue from IS. Data preserved. Continue?</div>
                  <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                    <button onClick={()=>setImplDisableWarning(null)} style={{padding:"7px 16px",borderRadius:6,border:"1px solid #D1D5DB",background:"#fff",color:"#4B5563",fontSize:11,fontWeight:600,cursor:"pointer"}}>Cancel</button>
                    <ViewOnly canEdit={canEditModel}><button onClick={confirmDisable} style={{padding:"7px 16px",borderRadius:6,border:"none",background:"#E07A5F",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}}>Disable</button></ViewOnly>
                  </div>
                </div>
              </div>}

              {lineEnabled && <ViewOnly canEdit={canEditModel}>
                <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:12}}>
                  {/* Source dropdown */}
                  <div style={{minWidth:200}}>
                    <div style={iLblS}>Revenue Source</div>
                    <select value={currentSourceKey} onChange={e=>{const opt=sourceOptions.find(o=>o.key===e.target.value);if(opt)handleSourceChange(opt.value,opt.sourceId||null);}} style={{...iIS,textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                      {sourceOptions.map(opt=>{
                        const isUsed = usedImplSources[opt.key] && usedImplSources[opt.key] !== line.id;
                        return <option key={opt.key} value={opt.key} disabled={isUsed}>{opt.label}{isUsed?" (in use)":""}</option>;
                      })}
                    </select>
                  </div>
                  {/* 2026-10-27 -- THE FEE BASIS (Brian Q-C / Q-H). A stored NULL shows BLANK and Save is refused until one is chosen (no row
                      existed on any company when this shipped, so a blank here is always a new, unanswered line). In % mode the
                      per-client boxes are hidden -- still stored, inert (Q-A) -- and switching back shows them with their values. */}
                  <div style={{minWidth:220}}>
                    <div style={iLblS}>Fee basis</div>
                    <select value={edit.impl_basis||""} onChange={e=>setImplField(line.id,"impl_basis",e.target.value||null)} style={{...iIS,textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",border:edit.impl_basis?"1px solid #D0D5DD":"1px solid #E0A458"}}>
                      <option value="" disabled>{IMPL_BASIS_BLANK_LABEL}</option>
                      <option value={IMPL_BASIS_PER_CLIENT}>{IMPL_BASIS_LABELS[IMPL_BASIS_PER_CLIENT]}</option>
                      <option value={IMPL_BASIS_PCT_NEW_ACV}>{IMPL_BASIS_LABELS[IMPL_BASIS_PCT_NEW_ACV]}</option>
                    </select>
                    {!edit.impl_basis && <div style={{fontSize:10,color:"#B07219",marginTop:6,fontWeight:600}}>{IMPL_BASIS_REQUIRED_MESSAGE}</div>}
                  </div>
                  {isPctNewAcvBasis(edit.impl_basis) && <div style={{minWidth:150}}>
                    <div style={iLblS}>{PCT_NEW_ACV_LABEL}</div>
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <input type="text" value={implPctText[line.id] !== undefined ? implPctText[line.id] : pctNewAcvText(edit.pct_of_new_acv)} onChange={e=>{const t=e.target.value;setImplPctText(prev=>({...prev,[line.id]:t}));const pr=parsePctNewAcvText(t);if(!pr.error)setImplField(line.id,"pct_of_new_acv",pr.rate);}} onFocus={e=>e.target.select()} placeholder="0-100" style={{...iIS,width:70}} />
                      <span style={{fontSize:11,color:"#6B7280"}}>%</span>
                    </div>
                    <div style={iHelpS}>{PCT_NEW_ACV_HELP}</div>
                    {(implPctText[line.id] !== undefined ? parsePctNewAcvText(implPctText[line.id]).error : (edit.pct_of_new_acv == null ? PCT_NEW_ACV_REQUIRED_MESSAGE : null)) && <div style={{fontSize:10,color:"#E07A5F",marginTop:6,fontWeight:600}}>{implPctText[line.id] !== undefined ? parsePctNewAcvText(implPctText[line.id]).error : PCT_NEW_ACV_REQUIRED_MESSAGE}</div>}
                  </div>}
                  {!isPctNewAcvBasis(edit.impl_basis) && <>
                  {/* Avg Price */}
                  <div style={{minWidth:160}}>
                    <div style={iLblS}>Avg Contract Value ($)</div>
                    <input type="text" defaultValue={edit.avg_price?edit.avg_price.toLocaleString("en-US"):""} key={"ap-"+line.id+"-"+edit.avg_price} onFocus={e=>{e.target.value=edit.avg_price?String(edit.avg_price):"";e.target.select();}} onBlur={e=>{const v=parseFloat(e.target.value.replace(/,/g,""))||0;setImplField(line.id,"avg_price",v);e.target.value=v?v.toLocaleString("en-US"):"";}} style={{...iIS,width:130}} />
                    <div style={iHelpS}>Average ARR per new client. Used to calculate number of new clients.</div>
                  </div>
                  {/* Impl Fee */}
                  <div style={{minWidth:180}}>
                    <div style={iLblS}>Implementation Fee per Client ($)</div>
                    <input type="text" defaultValue={edit.impl_fee?edit.impl_fee.toLocaleString("en-US"):""} key={"if-"+line.id+"-"+edit.impl_fee} onFocus={e=>{e.target.value=edit.impl_fee?String(edit.impl_fee):"";e.target.select();}} onBlur={e=>{const v=parseFloat(e.target.value.replace(/,/g,""))||0;setImplField(line.id,"impl_fee",v);e.target.value=v?v.toLocaleString("en-US"):"";}} style={{...iIS,width:130}} />
                  </div>
                  {/* Take Rate */}
                  <div style={{minWidth:130}}>
                    <div style={iLblS}>Take Rate (%)</div>
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <input type="text" value={implTakeText[line.id] !== undefined ? implTakeText[line.id] : takeRateText(edit.take_rate)} onChange={e=>{const t=e.target.value;setImplTakeText(prev=>({...prev,[line.id]:t}));const tr=parseTakeRateText(t);if(!tr.error)setImplField(line.id,"take_rate",tr.rate);}} onFocus={e=>e.target.select()} placeholder="0-100" style={{...iIS,width:70}} />
                      <span style={{fontSize:11,color:"#6B7280"}}>%</span>
                    </div>
                    <div style={iHelpS}>% of new clients purchasing implementation services</div>
                    {implTakeText[line.id] !== undefined && parseTakeRateText(implTakeText[line.id]).error && <div style={{fontSize:10,color:"#E07A5F",marginTop:6,fontWeight:600}}>{parseTakeRateText(implTakeText[line.id]).error}</div>}
                  </div>
                  </>}
                  {/* Recognition Mode */}
                  <div style={{minWidth:180}}>
                    <div style={iLblS}>Revenue Recognition</div>
                    <select value={edit.recognition_mode||'immediate'} onChange={e=>setImplField(line.id,"recognition_mode",e.target.value)} style={{...iIS,textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                      <option value="immediate">Immediate</option>
                      {/* ★ PART D — "Deferred (12 months)" WAS A LIE AFTER PART B. The term is the
                          line's own length_months, not 12; implementation is the NON-RENEWING
                          engine and its 12 was never a contract year (§12.5 rule 28). */}
                      <option value="deferred">Deferred (over the recognition length)</option>
                    </select>
                    <div style={iHelpS}>{edit.recognition_mode==='deferred'
                      ? (edit.length_months>0 ? `Fee recognized evenly over ${edit.length_months} months, then stops. GAAP compliant.` : "Fee recognized evenly over the recognition length below, then stops. GAAP compliant.")
                      : "Full implementation fee recognized in booking month"}</div>
                  </div>
                  {/* ★★ PART D — THE PER-COHORT RECOGNITION LENGTH. Only shown for DEFERRED: an
                      immediate line amortises nothing, so asking for a length there would put a
                      required-looking blank on correct data (§12.5 shape 10). */}
                  {edit.recognition_mode==='deferred' && <div style={{minWidth:150}}>
                    <div style={iLblS}>Recognition Length</div>
                    <input type="text"
                      value={edit.length_months==null?"":String(edit.length_months)}
                      onChange={e=>{const raw=e.target.value.replace(/[^0-9]/g,"");setImplField(line.id,"length_months",raw===""?null:parseInt(raw,10));}}
                      placeholder="e.g. 6"
                      style={{...iIS,width:90,border:`1px solid ${edit.length_months>0?"#D0D5DD":"#E53935"}`}} />
                    {/* ⛔ NO DEFAULT IS OFFERED AND NO PLACEHOLDER IS TREATED AS A VALUE. A missing
                        length REFUSES (Part B) rather than becoming 12 — §E1: a blank is flagged,
                        never silently defaulted. Part A gave the column no DEFAULT so this state
                        stays reachable. */}
                    <div style={iHelpS}>Months each engagement's fee is recognized over. Required — a blank is refused, not defaulted.</div>
                  </div>}
                </div>

                {/* ═══ ★★ PART D — THE INCOME STATEMENT LINE PICKER, THREE STATES ═══
                    Mirrors misc's control at the top of its editor (the "Create new IS line" /
                    "Route to existing IS line" pair), with the THIRD state misc does not have. */}
                <div style={{marginBottom:12,padding:"10px 12px",background:implRouteState==='routed'?"#EEF4FB":"#fff",borderRadius:6,border:"1px solid #E2E5F0"}}>
                  <div style={iLblS}>Income Statement Line</div>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <select
                      value={implRouteState}
                      onChange={e=>{
                        const v = e.target.value;
                        if (v === 'unanswered') setImplField(line.id,"target_gl_line_key",null);
                        else if (v === 'separate') setImplField(line.id,"target_gl_line_key",implSentinel);
                        else setImplField(line.id,"target_gl_line_key", (edit.target_gl_line_key && edit.target_gl_line_key!==implSentinel) ? edit.target_gl_line_key : (implAvailable[0]?.id || ""));
                      }}
                      style={{...iIS,textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",maxWidth:260,border:implRouteState==='unanswered'?"1px solid #E0A458":"1px solid #D0D5DD"}}>
                      {/* ⛔ THE UNANSWERED STATE IS OFFERED EXPLICITLY, not left as an empty select.
                          It is a real, distinct state — "nobody has decided yet" — and the operator
                          must be able to see that they are in it. */}
                      <option value="unanswered">— Not set —</option>
                      <option value="separate">Keep a separate forecast line</option>
                      <option value="routed" disabled={implAvailable.length===0 && implRouteState!=='routed'}>Route to an existing IS line</option>
                    </select>
                    {implRouteState==='routed' && (
                      <select value={edit.target_gl_line_key||""} onChange={e=>setImplField(line.id,"target_gl_line_key",e.target.value)}
                        style={{...iIS,textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",maxWidth:280}}>
                        {/* ⛔ NO NAME-GUESS PRE-FILL AND NO PRE-SELECTED "BEST MATCH". A tenant may
                            carry BOTH a "Professional Services" line and an "Implementation
                            Revenue" line, and nothing may guess which is which (§E1).
                            ⚠ The Step-7 DR picker USED TO name-guess (token overlap) -- a defect in the
                            precedent, measured 09-14 and retired by W-010 (09-24); it was never copied here. */}
                        {edit.target_gl_line_key && !implAvailable.some(l=>l.id===edit.target_gl_line_key) &&
                          <option value={edit.target_gl_line_key}>{implRouteLabel(edit.target_gl_line_key)}</option>}
                        {implAvailable.map(l=><option key={l.id} value={l.id}>{l.label}</option>)}
                      </select>
                    )}
                    {/* ★ THE CONSEQUENCE TEXT — an operator must be able to tell the three states
                        apart WITHOUT inferring one from the absence of another. */}
                    {implRouteState==='routed'
                      ? <span style={{fontSize:9,color:"#3D85C6",fontWeight:600}}>→ {implRouteLabel(edit.target_gl_line_key)} (existing IS line — no separate row)</span>
                      : implRouteState==='separate'
                        ? <span style={{fontSize:9,color:"#8B8FA3"}}>emits its own line ({implSentinel})</span>
                        : <span style={{fontSize:9,color:"#B07219",fontWeight:600}}>not set — defaults to its own line ({implSentinel}); choose one so the intent is recorded</span>}
                  </div>
                  {implRouteErr && <div style={{fontSize:10,color:"#E07A5F",marginTop:6,fontWeight:600}}>{implRouteErr}</div>}
                </div>

                {/* ★★ PART D — THE LENGTH REFUSAL'S READER. Part B computes this and projects it
                    as implLengthWarnings; until now NOTHING rendered it, so a deferred line with
                    no length was excluded from the forecast SILENTLY — the §E1 failure the
                    fail-loud exists to prevent. ⛔ A fail-loud that reaches nobody is not one. */}
                {implLenWarn && <div style={{marginBottom:12,padding:"10px 12px",background:"#FDF3E7",borderRadius:6,border:"1px solid #E0A458"}}>
                  <div style={{fontSize:10,color:"#B07219",fontWeight:700,marginBottom:2}}>This line is excluded from the forecast</div>
                  <div style={{fontSize:10,color:"#7A5C2E",lineHeight:1.45}}>{implLenWarn.message}</div>
                </div>}

                {/* Standalone monthly amounts — only visible when source_type = 'standalone' */}
                {edit.source_type==='standalone' && <div style={{marginBottom:12}}>
                  <div style={iLblS}>Monthly New ACV (manual)</div>
                  <div style={{fontSize:9,color:"#8B8FA3",marginBottom:6}}>Enter new ACV each month for standalone implementation revenue</div>
                  <div style={{display:"flex",gap:4}}>
                    {forecastMonths.map(m=>(
                      <div key={m} style={{flex:1,minWidth:0,maxWidth:80}}>
                        <div style={{fontSize:8,color:"#8B8FA3",textAlign:"center",marginBottom:2}}>{formatMonthLabel(m, "shortYear")}</div>
                        <input type="text" defaultValue={(localSa[m]||0)?(localSa[m]||0).toLocaleString("en-US"):""} key={"sa-"+line.id+"-"+m+"-"+(localSa[m]||0)} onFocus={e=>{const v=localSa[m]||0;e.target.value=v?String(v):"";e.target.select();}} onBlur={e=>{const v=parseFloat(e.target.value.replace(/,/g,""))||0;setImplField(line.id,"standalone_amounts",{...localSa,[m]:v});e.target.value=v?v.toLocaleString("en-US"):"";}} placeholder="0" style={{...iIS,width:"100%",padding:"3px 4px",fontSize:9,textAlign:"right"}} />
                      </div>
                    ))}
                  </div>
                </div>}

                {/* Display table */}
                <div style={{overflowX:"auto",marginBottom:12,border:"1px solid #E2E5F0",borderRadius:6}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,fontFamily:"'Space Mono',monospace"}}>
                    <thead>
                      <tr style={{background:"#F5F6FA"}}>
                        <th style={{padding:"6px 10px",textAlign:"left",fontSize:10,fontWeight:700,color:"#6B7280",position:"sticky",left:0,background:"#F5F6FA",zIndex:5,minWidth:180}}></th>
                        {forecastMonths.map(m=><th key={m} style={{padding:"6px 6px",textAlign:"right",fontSize:9,fontWeight:600,color:"#6B7280",minWidth:75,whiteSpace:"nowrap"}}>{formatMonthLabel(m, "shortYear")}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {/* 2026-10-27: the rows follow the basis the ENGINE applied (its returned impl_basis -- the saved state, like every
                          figure in this table): % of new ACV has no client count, gross or net step. */}
                      {(isPctNewAcvBasis(wfBasis) ? [
                        ["Source New ACV","sourceNewACV"],
                        [`Implementation (${pctNewAcvText(wfPct)}% of new ACV)`,"netImpl"],
                        ["GAAP Revenue Recognized","gaapRevenue"],
                        ...(edit.recognition_mode==='deferred'?[["Deferred Balance","deferredBalance"]]:[]),
                      ] : [
                        ["Source New ACV","sourceNewACV"],
                        ["New Clients","newClients"],
                        ["Gross Implementation","grossImpl"],
                        ["Net Implementation","netImpl"],
                        ["GAAP Revenue Recognized","gaapRevenue"],
                        ...(edit.recognition_mode==='deferred'?[["Deferred Balance","deferredBalance"]]:[]),
                      ]).map(([label,key])=>{
                        const isBold = key==='gaapRevenue'||key==='deferredBalance';
                        const bg = isBold?"#FAFBFE":"#fff";
                        return <tr key={key}>
                          <td style={{padding:"5px 10px",fontSize:10,fontWeight:isBold?600:400,color:"#1A1C2E",position:"sticky",left:0,background:bg,zIndex:5,borderBottom:"1px solid #F5F6FA",whiteSpace:"nowrap"}}>{label}</td>
                          {forecastMonths.map(m=>{
                            const v = lineRev[key]?.[m]||0;
                            const display = key==='newClients' ? (v===0?"\u2014":String(Math.round(v))) : (v===0?"\u2014":fmt(v));
                            return <td key={m} style={{padding:"5px 6px",textAlign:"right",fontSize:10,fontWeight:isBold?600:400,color:"#1A1C2E",background:bg,borderBottom:"1px solid #F5F6FA",fontFamily:"'Space Mono',monospace",whiteSpace:"nowrap"}}>{display}</td>;
                          })}
                        </tr>;
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Save button */}
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <button onClick={()=>saveImplLine(line.id)} disabled={saving} style={{padding:"7px 20px",borderRadius:6,border:"none",background:saving?"#A0A3B8":"linear-gradient(135deg,#3D85C6,#5A5FC0)",color:"#fff",fontSize:11,fontWeight:700,cursor:saving?"not-allowed":"pointer"}}>{saving?"Saving...":"Save"}</button>
                  {saved && <span style={{fontSize:11,fontWeight:600,color:"#81B29A"}}>Saved</span>}
                  {implSaveError[line.id] && <span style={{fontSize:11,fontWeight:600,color:"#E07A5F"}}>{implSaveError[line.id]}</span>}
                </div>
                {/* THE SAVE NOTE (Brian, 2026-09-15). The waterfall reads PERSISTED state, by
                    design: its cells resolve lineRev -> implRev -> ctxImplRevenue -> the model,
                    which is built from GET /implementation-lines. So an edit does not move a
                    figure until it is saved, and without a note that reads as a bug.
                    - The sentence is about the FIGURES, deliberately. The one thing in this
                      table that DOES track the form is whether the Deferred Balance ROW is
                      listed (edit.recognition_mode === 'deferred', in the row array above):
                      its VISIBILITY follows the select while its VALUES stay saved. Wording it
                      about the figures keeps it true; a claim about the row LIST would not be.
                    - It says "above" because the table is above this button, not below it.
                    - Reuses iHelpS, the page's existing caption style. NO new component, no new
                      styling primitive, no icon, and NOT amber -- amber already means
                      "routing unanswered" on this panel and must keep meaning only that. */}
                {canEditModel?<div style={iHelpS}>The figures above reflect the last saved values, so changes appear after you Save.</div>:<div style={iHelpS}><ViewOnlyNote/></div>}
              </ViewOnly>}
            </div>;
          })}
        </div>;
      })()}
    </div>
  );
}
